//
// Copyright 2023 DXOS.org
//

import { next as A, type Heads, getHeads } from '@automerge/automerge';
import { type AutomergeUrl, type DocumentId } from '@automerge/automerge-repo';
import * as EffectContext from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Option from 'effect/Option';
import * as Stream from 'effect/Stream';

import {
  type CleanupFn,
  Event,
  type ReadOnlyEvent,
  TimeoutError,
  Trigger,
  TriggerState,
  UpdateScheduler,
  asyncTimeout,
  runInContextAsync,
  scheduleTaskInterval,
} from '@dxos/async';
import { Context, ContextDisposedError, cancelWithContext } from '@dxos/context';
import { raise, warnAfterTimeout } from '@dxos/debug';
import { type Database, type Entity, Ref } from '@dxos/echo';
import {
  BACKED_OBJECT_LIMIT,
  type BranchRecord,
  DatabaseDirectory,
  EncodedReference,
  type EntityStructure,
  SpaceDocVersion,
  type SpaceState,
} from '@dxos/echo-protocol';
import {
  type RefResolver,
  type RefResolverRequest,
  afterCommit,
  batchEvents,
  objectStructureFromJson,
  objectStructureToJson,
  runInCommit,
} from '@dxos/echo/internal';
import { assertState, invariant } from '@dxos/invariant';
import { EID, type EntityId, type PublicKey, type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';
import { RpcClosedError, runServiceCall, runServiceEffect, subscribeStream } from '@dxos/protocols';
import type { DataService, QueryService } from '@dxos/protocols/rpc';
import { trace } from '@dxos/tracing';
import { ComplexSet, chunkArray, countWork, deepMapValues } from '@dxos/util';

import {
  type ChangeEvent,
  type ClientDocHandleEvents,
  type DocHandleProxy,
  RepoProxy,
  type SaveStateChangedEvent,
  toDocumentId,
} from '../automerge/index.ts';
import { DocumentUnavailableError, EchoClientError, RepoClosedError } from '../errors.ts';
import { type HypergraphImpl } from '../hypergraph.ts';
import { type BranchStore, forkDump, referencedObjectIds } from './branching.ts';
import { ObjectCoreRegistry } from './object-core-registry.ts';
import { type IDatabaseBinding, ObjectCore, type SnapshotState } from './object-core.ts';
import { shareStructure } from './share-structure.ts';
import {
  type AddCoreOptions,
  type AtomicReplaceObjectProps,
  type DocumentChanges,
  type GetObjectCoreByIdOptions,
  type ItemsUpdatedEvent,
  type LoadObjectDocumentOptions,
  type LoadObjectOptions,
  type ReleaseObjectOptions,
  type SpaceDocumentHeads,
} from './types.ts';
import { getInlineAndLinkChanges, getRemovedObjectIds } from './util.ts';

const TRACE_LOADING = false;

/** Ceiling on db update emissions per second while a bulk delivery keeps arriving. */
const DB_UPDATE_MAX_FREQ = 10;

/** A satisfaction request that will not change again without a new load. */
const isSettled = (request: RefResolverRequest): boolean =>
  request.state === 'ready' || request.state === 'unavailable';

/**
 * Payload for the internal object-document-loaded notification.
 */
interface ObjectDocumentLoaded {
  handle: DocHandleProxy<DatabaseDirectory>;
  objectId: string;
}

/**
 * Payload for the internal object-unavailable notification.
 */
interface ObjectUnavailable {
  handle?: DocHandleProxy<DatabaseDirectory>;
  objectId: string;
}

type SpaceDocumentLinks = DatabaseDirectory['links'];

/** Which loaded object documents a space keeps in memory. */
export type DocumentRetention = {
  /** How long, in ms, a document stays loaded once nothing changes it. */
  ttl?: number;
  /** How many of the most recently edited documents stay loaded whatever their idle time. */
  budget?: number;
};

export type EntityManagerProps = {
  graph: HypergraphImpl;
  dataService: DataService.Client;
  queryService: QueryService.Client;
  runtime: EffectContext.Context<never>;
  spaceId: SpaceId;
  spaceKey: PublicKey;
  /** Device-local persistence for the current-branch selection (non-synced). In-memory if omitted. */
  branchStore?: BranchStore;

  /** Which loaded documents go back to their stored changes; see {@link DocumentRetention}. */
  documentRetention?: DocumentRetention;

  /** Mints the caller-facing proxy for a core, injected because the proxy layer is built on this one. */
  createEntity: (core: ObjectCore) => Entity.Unknown;
};

/**
 * Core database implementation: owns all Automerge document management, object
 * storage, dependency tracking, and update scheduling for a single space.
 * DatabaseImpl holds a reference to this class and delegates all document and
 * object-core operations here.
 */
export class EntityManager implements IDatabaseBinding {
  private readonly _spaceKey: PublicKey;
  private readonly _spaceId: SpaceId;
  private readonly _hypergraph: HypergraphImpl;
  private _dataService: DataService.Client;
  private _queryService: QueryService.Client;
  private readonly _runtime: EffectContext.Context<never>;
  readonly _repoProxy: RepoProxy;

  // ── Object storage ──────────────────────────────────────────────────────
  /**
   * Loaded cores, held weakly: a space holds what the caller has open rather than everything it has
   * ever read. A collected core takes its document handle and satisfaction request with it — see
   * {@link _releaseObject}.
   */
  private readonly _objects = new ObjectCoreRegistry({ onRelease: (id) => this._releaseObject(id) });

  /**
   * Device-local, non-synced: object id -> currently-selected branch name (`'main'` omitted).
   * Hydrated from {@link _branchStore} on open (if provided) and persisted on switch.
   */
  private readonly _currentBranches = new Map<string, string>();

  /** Optional device-local persistence for {@link _currentBranches} (survives reload, never syncs). */
  private readonly _branchStore?: BranchStore;

  private readonly _createEntity: (core: ObjectCore) => Entity.Unknown;

  private readonly _documentRetention: Required<DocumentRetention>;

  /**
   * Object ids whose backing document was determined to be not on local disk
   * via a `diskOnly` load probe. Used by `loadObjectCoreById` to bail out
   * of the wait without resolving when the doc would otherwise require
   * network delivery — turning previously infinite stalls into a fast
   * `undefined` return.
   */
  private readonly _unavailableObjects = new Set<EntityId>();

  /**
   * Per-entity closure-aware satisfaction requests, with the unsubscribes that keep the database
   * context's dispose list proportional to them. Strong-dependency satisfaction is delegated to the
   * {@link RefResolver}: each surfaced entity holds a disk-bound request whose `ready` state is the
   * surface gate, spanning same-space db, cross-space db, feed queues, and the registry.
   *
   * Neither map holds the entity: a request reaches it only through its load op, whose result is weak
   * ({@link LoadOp.result}), so an object is released with its core rather than pinned by the gate
   * that surfaced it.
   */
  private readonly _satisfactionRequests = new Map<EntityId, RefResolverRequest>();
  private readonly _satisfactionSubscriptions = new Map<EntityId, CleanupFn>();

  private _refResolver: RefResolver | undefined;

  private _ctx: Context | undefined;

  // ── Events ──────────────────────────────────────────────────────────────
  readonly _updateEvent = new Event<ItemsUpdatedEvent>();
  /** Objects a local commit wrote, emitted synchronously after the commit and before {@link _updateEvent}. */
  readonly _localCommitEvent = new Event<ItemsUpdatedEvent>();
  readonly saveStateChanged: ReadOnlyEvent<SaveStateChangedEvent>;

  /** Fires when the database has finished loading its initial space root document. */
  readonly opened = new Trigger();

  /** Fires after any branch operation (create / switch / merge / delete) for reactive branch UI. */
  readonly branchesChanged = new Event<void>();

  /** Fires when service connection is re-established after a leader change. */
  private readonly _reconnected = new Event<void>();

  /** Public view of {@link EntityManager._reconnected}, so sibling streams can re-establish too. */
  get reconnected(): ReadOnlyEvent<void> {
    return this._reconnected;
  }

  // ── Automerge document state ────────────────────────────────────────────
  private _spaceRootDocHandle: DocHandleProxy<DatabaseDirectory> | null = null;

  private readonly _objectDocumentHandles = new Map<string, DocHandleProxy<DatabaseDirectory>>();

  /**
   * Object ids per document, the inverse of {@link _objectDocumentHandles}. Releasing one object's
   * core must not drop a document another loaded object is still mounted in, and a linked document
   * can hold several — so the last object out is what makes the handle evictable, which this answers
   * without a scan of the loaded set.
   */
  private readonly _documentObjects = new Map<DocHandleProxy<DatabaseDirectory>, Set<string>>();

  private readonly _objectsPendingDocumentLoad = new Map<string, LoadObjectDocumentOptions>();

  private readonly _currentlyLoadingObjects = new ComplexSet<{ url: AutomergeUrl; objectId: string }>(
    ({ url, objectId }) => `${url}:${objectId}`,
  );

  private readonly _pendingDocumentCreations = new Map<string, Promise<void>>();

  // ── Update scheduling ────────────────────────────────────────────────────
  private _objectsForNextDbUpdate = new Set<string>();
  /** Objects the current local commit wrote, emitted by {@link #flushLocalCommit} once it ends. */
  readonly #objectsForLocalCommit = new Set<string>();
  /** Objects written locally since the last flush that reached the index, with the sequence of their last write. */
  readonly #unindexedWrites = new Map<string, number>();
  #writeSequence = 0;
  private _objectsForNextUpdate = new Set<string>();
  private _updateScheduler!: UpdateScheduler;

  // ── Private event field ──────────────────────────────────────────────────
  private readonly _rootChangedEvent = new Event<void>();

  /** Object ids the space root has started routing, as it gains them. */
  private readonly _linksAddedEvent = new Event<string[]>();

  /** Ids {@link _linksAddedEvent} has already reported. */
  #linkedObjectIds = new Set<string>();

  /** Set from {@link close} until the next {@link open}: the working set is gone, not merely unloaded. */
  #closed = false;

  #rearmOpened(): void {
    if (this.opened.state !== TriggerState.WAITING) {
      this.opened.reset();
    }
  }

  #rejectOpenedUntilReopen(): void {
    this.#rearmOpened();
    this.opened.throw(new ContextDisposedError());
  }

  get #closing(): boolean {
    return this.#closed || this._ctx?.disposed === true;
  }

  /**
   * Bumped by every {@link open}. A load captures it and revalidates after each await: re-reading
   * {@link #closed} alone cannot distinguish a live lifetime from a close-and-reopen that happened
   * while the load was suspended, and would hand a pre-close caller a post-reopen result.
   */
  #generation = 0;

  /** Settles loads waiting on an update when {@link close} ends their lifetime, rather than leaving them pending. */
  readonly #lifetimeEnded = new Event<void>();

  constructor(options: EntityManagerProps) {
    this._createEntity = options.createEntity;
    this._spaceKey = options.spaceKey;
    this._spaceId = options.spaceId;
    this._hypergraph = options.graph;
    this._dataService = options.dataService;
    this._queryService = options.queryService;
    this._runtime = options.runtime;
    this._branchStore = options.branchStore;
    this._documentRetention = { ...DOCUMENT_RETENTION, ...options.documentRetention };
    this._repoProxy = new RepoProxy(this._dataService, this._runtime, this._spaceId);
    this.saveStateChanged = this._repoProxy.saveStateChanged;
  }

  get spaceId(): SpaceId {
    return this._spaceId;
  }

  /** @deprecated Use spaceId. */
  get spaceKey(): PublicKey {
    return this._spaceKey;
  }

  get rootChanged(): ReadOnlyEvent<void> {
    return this._rootChangedEvent;
  }

  get linksAdded(): ReadOnlyEvent<string[]> {
    return this._linksAddedEvent;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * Opens the entity manager: starts the repo and wires up update scheduling.
   * Call `openWithSpaceState` afterwards to load documents once a root URL is known.
   */
  async open(ctx: Context): Promise<void> {
    this._ctx = ctx;
    this.#closed = false;
    this.#generation++;
    this.#rearmOpened();
    // The rate only coalesces a bulk delivery from the host, where every emission re-runs each live
    // query and re-hydrates index results over the whole space; every other trigger skips the delay,
    // so a query reflects a local write, or a peer's single edit, at once.
    this._updateScheduler = new UpdateScheduler(ctx, async () => this._emitDbUpdateEvents(ctx), {
      maxFrequency: DB_UPDATE_MAX_FREQ,
    });

    await this._repoProxy.open();
    ctx.onDispose(() => this._unsubscribeFromHandles());
    scheduleTaskInterval(ctx, async () => this.#expireIdleDocuments(), this._documentRetention.ttl / 2);
    ctx.onDispose(() => {
      for (const { handle, onUpdate, onActivated } of this.#passiveHandles.values()) {
        handle.off('passive', onUpdate);
        handle.off('activated', onActivated);
      }
      this.#passiveHandles.clear();
    });
    ctx.onDispose(() => {
      for (const request of this._satisfactionRequests.values()) {
        request.abort();
      }
      this._satisfactionRequests.clear();
      for (const unsubscribe of this._satisfactionSubscriptions.values()) {
        unsubscribe();
      }
      this._satisfactionSubscriptions.clear();
    });
  }

  /**
   * Performs initial AM document load after a rootUrl is known.
   * Called by DatabaseImpl when the first space root is available.
   */
  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  async openWithSpaceState(ctx: Context, spaceState: SpaceState): Promise<void> {
    const start = performance.now();
    try {
      await this._loadSpaceRootDocHandle(ctx, spaceState);
      const spaceRootDocHandle = this.getSpaceRootDocHandle();
      const spaceRootDoc: DatabaseDirectory = spaceRootDocHandle.doc();
      invariant(spaceRootDoc);
      const objectIds = Object.keys(spaceRootDoc.objects ?? {});
      this._createInlineObjects(spaceRootDocHandle, objectIds);
      spaceRootDocHandle.on('change', this._onDocumentUpdate);
    } catch (err) {
      if (err instanceof ContextDisposedError) {
        return;
      }
      log.catch(err);
      throw err;
    }

    const elapsed = performance.now() - start;
    if (elapsed > 1_000) {
      log.warn('slow AM open', { docId: spaceState.rootUrl, duration: elapsed });
    }

    this.opened.wake();

    // Restore device-local branch selections (e.g. after a reload) and re-bind each branched subtree
    // to its selected branch — objects otherwise load on `main`.
    if (this._branchStore) {
      await this._hydrateCurrentBranches();
    }
  }

  private async _hydrateCurrentBranches(): Promise<void> {
    try {
      const entries = await this._branchStore!.load();
      // Only selections whose branch still exists are installed — `switchBranch` records the
      // selection itself, so a stale entry (deleted branch) never leaves `getCurrentBranch`
      // reporting a branch the core is not actually bound to.
      const reapplied = new Set<string>();
      for (const [objectId, name] of Object.entries(entries)) {
        if (name === 'main' || reapplied.has(objectId) || !this.getBranchRegistry(objectId)?.[name]) {
          continue;
        }
        reapplied.add(objectId);
        await this.switchBranch(objectId, name);
      }
    } catch (err) {
      log.warn('failed to hydrate current branches', { err });
    }
  }

  /**
   * Chains {@link BranchStore.save} calls so writes land in selection order — concurrent saves
   * resolving out of order could otherwise persist a stale selection map.
   */
  private _persistChain: Promise<void> = Promise.resolve();

  private _persistCurrentBranches(): void {
    if (!this._branchStore) {
      return;
    }
    const entries = Object.fromEntries(this._currentBranches);
    this._persistChain = this._persistChain
      .then(() => this._branchStore!.save(entries))
      .catch((err) => log.warn('failed to persist current branches', { err }));
  }

  /**
   * Update in response to a space root URL change after the initial load.
   */
  async updateSpaceState(ctx: Context, spaceState: SpaceState): Promise<void> {
    invariant(this._spaceRootDocHandle, 'Must be open');
    const currentRootUrl = this.getSpaceRootDocHandle().url;
    if (spaceState.rootUrl === currentRootUrl) {
      return;
    }
    this._unsubscribeFromHandles();
    const objectIdsToLoad = this._clearHandleReferences();

    try {
      await this._loadSpaceRootDocHandle(ctx, spaceState);
      const spaceRootDocHandle = this.getSpaceRootDocHandle();
      await this._handleSpaceRootDocumentChange(spaceRootDocHandle, objectIdsToLoad);
      spaceRootDocHandle.on('change', this._onDocumentUpdate);
      this._updateScheduler.forceTrigger(); // Flush notifications from the swap window.
    } catch (err) {
      if (err instanceof ContextDisposedError) {
        return;
      }
      log.catch(err);
      throw err;
    }
  }

  async close(): Promise<void> {
    this.#rejectOpenedUntilReopen();
    // A closed manager can be reopened (the database re-runs `openWithSpaceState`), and that path
    // creates a core for every inline object in the directory — so cores and handles surviving the
    // close would be re-created over themselves.
    this.#closed = true;
    this.#lifetimeEnded.emit();
    // Dropped before the proxy closes, so a change delivered during teardown cannot trigger a load
    // against a closed proxy.
    this._unsubscribeFromHandles();
    this._clearHandleReferences();
    this._objects.clear();
    this._unavailableObjects.clear();
    this._objectsPendingDocumentLoad.clear();
    this._currentlyLoadingObjects.clear();
    this._objectsForNextDbUpdate.clear();
    this.#objectsForLocalCommit.clear();
    this.#unindexedWrites.clear();
    this._objectsForNextUpdate.clear();
    await this._repoProxy.close();
  }

  /** Whether the lifetime a load started in has ended — closed, or already replaced by a reopen. */
  #isStale(generation: number): boolean {
    return this.#closed || this.#generation !== generation;
  }

  /**
   * A promise that rejects when the given lifetime ends, for a load to race its wait against — a
   * closed manager never satisfies a load's readiness predicate, so an unraced wait would hang
   * instead of settling. The caller races it immediately, so its rejection is always observed, and
   * disposes it once its wait settles.
   */
  #rejectWhenStale(generation: number): { promise: Promise<never>; dispose: CleanupFn } {
    let dispose: CleanupFn = () => {};
    const promise = new Promise<never>((_resolve, reject) => {
      if (this.#isStale(generation)) {
        reject(new ContextDisposedError());
        return;
      }
      dispose = this.#lifetimeEnded.on(() => reject(new ContextDisposedError()));
    });
    return { promise, dispose };
  }

  // ── Core object operations ───────────────────────────────────────────────

  /** @deprecated Return only loaded objects. */
  allObjectCores(): ObjectCore[] {
    return Array.from(this._objects.values());
  }

  /**
   * Objects written locally that the index may not reflect yet: everything written since the last flush that waited
   * for indexing. Only these can disagree with an index answer, since the index holds everything else this client has.
   */
  unindexedWrites(): Iterable<string> {
    return this.#unindexedWrites.keys();
  }

  getObjectCoreById(id: string, { load = true }: GetObjectCoreByIdOptions = {}): ObjectCore | undefined {
    // A closed database has an empty working set, and every caller of this synchronous read already
    // treats an absent core as unresolved — whereas throwing reaches the fire-and-forget query
    // paths that outlive the close (index hydration recomputing a result) as an unhandled
    // rejection. The throw below stays for a database that was never opened, which is a caller bug.
    if (this.#closed) {
      return undefined;
    }
    if (!this._spaceRootDocHandle) {
      throw new Error('Database is not ready.');
    }

    const objCore = this._objects.get(id) ?? this._rehydrateCore(id);
    if (!objCore) {
      if (load) {
        this._loadObjectDocument(id);
      }
      return undefined;
    }

    invariant(objCore instanceof ObjectCore);
    return objCore;
  }

  /**
   * Recreates a core from a document this space already holds — the case where the previous core was
   * collected but its document did not go with it (an inline object in the space root, or a linked
   * document whose release was deferred).
   *
   * Without this a re-read would wait on a document load that never comes: the loader short-circuits
   * on the handle it already has, so nothing would ever emit the update the read is waiting for.
   */
  private _rehydrateCore(objectId: string): ObjectCore | undefined {
    // Only the already-bound handle: deriving the document from the space root instead cost two
    // automerge lookups on every cold-read miss, enough to push an index hit past its load timeout.
    const handle = this._objectDocumentHandles.get(objectId);
    if (handle == null || !handle.isLoaded() || handle.doc()?.objects?.[objectId] == null) {
      return undefined;
    }
    const core = this._createObjectInDocument(handle, objectId);
    // The handle is whatever the object was last bound to, which is the branch document while a
    // branch is selected — so the fresh core has to carry that selection too, or `Obj.getBranch`
    // would report `main` for an object reading and writing a branch.
    core.branch = this.getCurrentBranch(objectId);
    return core;
  }

  /** The entity for an id already in the working set, keyed on `core.rootProxy` so no second identity map outlives it. */
  getEntityById(id: string, { deleted = false }: { deleted?: boolean } = {}): Entity.Unknown | undefined {
    const core = this.getObjectCoreById(id);
    if (!core || !core.isBodyAvailable || (core.isDeleted() && !deleted)) {
      return undefined;
    }
    return core.rootProxy ?? this._createEntity(core);
  }

  /**
   * Places an index row as a snapshot core over the row's stored changes, or returns the snapshot core
   * that already holds the object. A row this database does not take, or one that fails to decode or
   * place, returns undefined and loads through its document.
   */
  placeIndexRow(id: EntityId, documentId: string, decode: () => SnapshotState | undefined): Entity.Unknown | undefined {
    if (!this.#acceptsRow(id, documentId)) {
      return undefined;
    }
    const held = this.getObjectCoreById(id, { load: false });
    if (held) {
      return held.snapshot ? (held.rootProxy ?? this._createEntity(held)) : undefined;
    }
    if (this._objectDocumentHandles.has(id)) {
      return undefined;
    }
    try {
      const state = decode();
      if (!state) {
        return undefined;
      }
      const handle = this._repoProxy.find<DatabaseDirectory>(documentId as DocumentId, {
        passive: { bytes: state.bytes, heads: state.heads },
      });
      if (!handle.passiveHeads) {
        return undefined;
      }

      const core = new ObjectCore();
      core.id = id;
      core.entityManager = this;
      core.setSnapshot({ root: { objects: { [id]: state.structure } }, updatedAt: state.updatedAt, handle });
      this._objects.set(id, core);
      countWork('echo.snapshotObjects');
      this.#watchPassive(id, handle);
      this._markObjectAvailable(id);
      return this._createEntity(core);
    } catch (err) {
      log.warn('index row not placed, the object loads from its document', { id, err });
      return undefined;
    }
  }

  #acceptsRow(id: string, documentId: string): boolean {
    return !this.#closing && this.#routesTo(id, documentId) && this.getCurrentBranch(id) === 'main';
  }

  #routesTo(id: string, documentId: string | undefined): boolean {
    if (!this._spaceRootDocHandle) {
      return false;
    }
    return documentId !== undefined && this.getObjectDocumentId(id) === documentId;
  }

  #refreshSnapshot(core: ObjectCore, { structure, updatedAt }: Pick<SnapshotState, 'structure' | 'updatedAt'>): void {
    const { snapshot } = core;
    invariant(snapshot);
    const previous = snapshot.root.objects[core.id];
    const shared = shareStructure(previous, structure);
    core.setSnapshot({
      root: shared === previous ? snapshot.root : { objects: { [core.id]: shared } },
      updatedAt,
      handle: snapshot.handle,
    });
    if (shared !== previous) {
      core.notifyUpdate();
      this._scheduleThrottledDbUpdate([core.id], { coalesce: true });
    }
  }

  readonly #passiveHandles = new Map<
    string,
    {
      handle: DocHandleProxy<DatabaseDirectory>;
      onUpdate: (event: PassiveUpdate) => void;
      onActivated: () => void;
    }
  >();

  #watchPassive(id: string, handle: DocHandleProxy<DatabaseDirectory>): void {
    this.#unwatchPassive(id);
    const onUpdate = (event: PassiveUpdate) => this.#onPassiveUpdate(id, event);
    const onActivated = () => this.#onActivated(id, handle);
    handle.on('passive', onUpdate);
    handle.on('activated', onActivated);
    this.#passiveHandles.set(id, { handle, onUpdate, onActivated });
  }

  #unwatchPassive(id: string, { released = false }: { released?: boolean } = {}): void {
    const entry = this.#passiveHandles.get(id);
    if (entry === undefined) {
      return;
    }
    this.#passiveHandles.delete(id);
    entry.handle.off('passive', entry.onUpdate);
    entry.handle.off('activated', entry.onActivated);
    if (released && entry.handle.passiveHeads && entry.handle.documentId) {
      this._repoProxy.release(entry.handle.documentId);
    }
  }

  #onPassiveUpdate(id: string, { handle, heads, json, updatedAt }: PassiveUpdate): void {
    const core = this._objects.get(id);
    if (this.#closed || core?.snapshot?.handle !== handle || !handle.passiveHeads) {
      return;
    }
    if (json === undefined) {
      handle._activate();
      return;
    }
    const structure = objectStructureFromJson(JSON.parse(json), {
      createdAt: core.snapshot.root.objects[id]?.system?.createdAt,
    });
    this.#refreshSnapshot(core, { structure, updatedAt });
  }

  /**
   * Moves each object whose document has not changed for the retention TTL, that no editor holds and that
   * is not among the most recently edited, back onto its document's stored changes, subscribed passive, so
   * the document leaves memory. The next write or document read loads it again.
   */
  #expireIdleDocuments(): void {
    const { ttl, budget } = this._documentRetention;
    const now = performance.now();
    const cores = this.allObjectCores();
    // Wasm memory never shrinks, so a release only makes room for a later load, and a document edited
    // again would load right back.
    // TODO(wittjosiah): Consider ranking by how often a document is edited or loaded instead of when.
    const kept = new Set(
      cores
        .filter((core) => core.editedAt > 0 && this.#isExpirable(core))
        .sort((a, b) => b.editedAt - a.editedAt)
        .slice(0, budget),
    );
    for (const core of cores) {
      if (!kept.has(core) && now - core.activeAt >= ttl && this.#isExpirable(core)) {
        this.#expire(core);
      }
    }
  }

  /** Whether the core is the only object bound to its own document, on the main branch, with no editor. */
  #isExpirable(core: ObjectCore): boolean {
    const handle = core.docHandle;
    return (
      handle !== undefined &&
      handle !== this._spaceRootDocHandle &&
      !core.hasAccessorListeners &&
      this.getCurrentBranch(core.id) === 'main' &&
      this._objectDocumentHandles.get(core.id) === handle &&
      this._documentObjects.get(handle)?.size === 1
    );
  }

  /** Backs the core with a snapshot of its document as it is now, when the host can keep that current. */
  #expire(core: ObjectCore): void {
    const handle = core.docHandle;
    const documentId = handle?.documentId;
    if (!handle || !documentId) {
      return;
    }
    const doc = handle.doc();
    const structure = doc.objects?.[core.id];
    // The host's passive updates carry JSON only under these conditions; otherwise the object loads again.
    if (
      !structure ||
      Object.keys(doc.objects ?? {}).length !== 1 ||
      Object.keys(doc.links ?? {}).length > 0 ||
      structure.system?.mergedInto !== undefined
    ) {
      return;
    }
    const json = JSON.stringify(objectStructureToJson(core.id as EntityId, structure));
    const bytes = A.save(doc);
    if (json.length > BACKED_OBJECT_LIMIT || bytes.length > BACKED_OBJECT_LIMIT) {
      return;
    }
    const heads = A.getHeads(doc);
    const updatedAt = core.getUpdatedAt();
    if (!this._repoProxy.deactivate(documentId, { bytes, heads })) {
      return;
    }
    this._unbindObjectDocument(core.id);
    if (handle.url != null) {
      this._currentlyLoadingObjects.delete({ url: handle.url, objectId: core.id });
    }
    core.demote({
      root: {
        objects: {
          [core.id]: objectStructureFromJson(JSON.parse(json), { createdAt: structure.system?.createdAt }),
        },
      },
      updatedAt,
      handle,
    });
    countWork('echo.documentsExpired');
    this.#watchPassive(core.id, handle);
  }

  /** Whoever loaded a snapshot core's document, this binds the core to it. */
  #onActivated(id: string, handle: DocHandleProxy<DatabaseDirectory>): void {
    if (this.#closed || this._objects.get(id)?.snapshot?.handle !== handle) {
      return;
    }
    this._onObjectDocumentLoaded({ handle, objectId: id });
  }

  endSnapshot(id: string): void {
    this.#unwatchPassive(id, { released: true });
  }

  /**
   * Waits for a held core's strong dependencies to settle, then hands it out by the same rule as
   * {@link loadEntityById}.
   */
  async settleEntity(
    core: ObjectCore,
    { allowDeleted = false }: { allowDeleted?: boolean } = {},
  ): Promise<Entity.Unknown | undefined> {
    await this.#waitForStrongDeps(core);
    return this.#publishable(core, { allowDeleted });
  }

  async #waitForStrongDeps(core: ObjectCore): Promise<void> {
    if (this.#closed) {
      throw new ContextDisposedError();
    }
    const generation = this.#generation;
    const request = this._ensureSatisfactionRequest(core);
    if (request.state !== 'ready' && request.state !== 'unavailable') {
      const cancellation = this.#rejectWhenStale(generation);
      try {
        await Promise.race([request.wait(), cancellation.promise]);
      } finally {
        cancellation.dispose();
      }
    }
  }

  /** Like {@link getEntityById}, but loads the object's document first. */
  async loadEntityById(
    objectId: string,
    { allowDeleted = false, ...options }: LoadObjectOptions & { allowDeleted?: boolean } = {},
  ): Promise<Entity.Unknown | undefined> {
    const core = await this.loadObjectCoreById(objectId, options);
    return (
      core && this.#publishable(core, { allowDeleted, returnWithUnsatisfiedDeps: options.returnWithUnsatisfiedDeps })
    );
  }

  #publishable(
    core: ObjectCore,
    { allowDeleted, returnWithUnsatisfiedDeps }: { allowDeleted: boolean; returnWithUnsatisfiedDeps?: boolean },
  ): Entity.Unknown | undefined {
    if (!this._coreOrUndefined(core, returnWithUnsatisfiedDeps) || (core.isDeleted() && !allowDeleted)) {
      return undefined;
    }
    return core.rootProxy ?? this._createEntity(core);
  }

  async loadObjectCoreById(
    objectId: string,
    { timeout, returnWithUnsatisfiedDeps, diskOnly }: LoadObjectOptions = {},
  ): Promise<ObjectCore | undefined> {
    const generation = this.#generation;
    if (this.#closed) {
      throw new ContextDisposedError();
    }
    if (diskOnly && this._unavailableObjects.has(objectId)) {
      return undefined;
    }
    const cachedCore = this.getObjectCoreById(objectId, { load: false });
    if (cachedCore && this._isCoreResolved(cachedCore, returnWithUnsatisfiedDeps)) {
      return this._coreOrUndefined(cachedCore, returnWithUnsatisfiedDeps);
    }

    const isReady = () => {
      if (diskOnly && this._unavailableObjects.has(objectId)) {
        return true;
      }
      const core = this.getObjectCoreById(objectId, { load: false });
      return core != null && this._isCoreResolved(core, returnWithUnsatisfiedDeps);
    };

    // Subscribed explicitly rather than through `Event.waitFor`, which unsubscribes only when its
    // predicate matches and returns no handle — so a wait the cancellation below wins would leave
    // its predicate running on every later update, for every load a teardown interrupted.
    let disposeUpdateListener: CleanupFn = () => {};
    const waitForUpdate = new Promise<void>((resolve) => {
      disposeUpdateListener = this._updateEvent.on((event) => {
        if (event.itemsUpdated.some(({ id }) => id === objectId) && isReady()) {
          resolve();
        }
      });
    });
    this._loadObjectDocument(objectId, { diskOnly });

    const cancellation = this.#rejectWhenStale(generation);
    try {
      await Promise.race([timeout ? asyncTimeout(waitForUpdate, timeout) : waitForUpdate, cancellation.promise]);
    } finally {
      disposeUpdateListener();
      cancellation.dispose();
    }
    if (this.#isStale(generation)) {
      throw new ContextDisposedError();
    }

    if (diskOnly && this._unavailableObjects.has(objectId)) {
      return undefined;
    }
    const finalCore = this.getObjectCoreById(objectId, { load: false });
    if (!finalCore) {
      return undefined;
    }
    return this._coreOrUndefined(finalCore, returnWithUnsatisfiedDeps);
  }

  private _isCoreResolved(core: ObjectCore, returnWithUnsatisfiedDeps?: boolean): boolean {
    // A core whose body has not landed carries nothing to read, so a load waits on rather than
    // resolves with it.
    if (!core.isBodyAvailable) {
      return false;
    }
    if (returnWithUnsatisfiedDeps || this._areDepsSatisfied(core)) {
      return true;
    }
    return this._areDepsResolved(core);
  }

  private _coreOrUndefined(core: ObjectCore, returnWithUnsatisfiedDeps?: boolean): ObjectCore | undefined {
    if (!core.isBodyAvailable) {
      return undefined;
    }
    if (returnWithUnsatisfiedDeps || this._areDepsSatisfied(core)) {
      return core;
    }
    return undefined;
  }

  async batchLoadObjectCores(
    objectIds: string[],
    {
      inactivityTimeout = 30_000,
      returnDeleted = false,
      returnWithUnsatisfiedDeps = false,
      failOnTimeout = false,
    }: {
      inactivityTimeout?: number;
      returnDeleted?: boolean;
      returnWithUnsatisfiedDeps?: boolean;
      failOnTimeout?: boolean;
    } = {},
  ): Promise<(ObjectCore | undefined)[]> {
    const generation = this.#generation;
    if (this.#closed) {
      throw new ContextDisposedError();
    }
    if (!this._spaceRootDocHandle) {
      throw new Error('Database is not ready.');
    }

    const result: (ObjectCore | undefined)[] = new Array(objectIds.length);
    const objectsToLoad: Array<{ id: string; resultIndex: number }> = [];
    for (let i = 0; i < objectIds.length; i++) {
      const objectId = objectIds[i];

      if (!this._objectPresent(objectId)) {
        result[i] = undefined;
        continue;
      }

      // A core whose body has not landed reads as nothing, so it counts as still loading.
      const loadedCore = this.getObjectCoreById(objectId, { load: true });
      const core = loadedCore?.isBodyAvailable ? loadedCore : undefined;
      if (!returnDeleted && this._objects.get(objectId)?.isDeleted()) {
        result[i] = undefined;
      } else if (!returnWithUnsatisfiedDeps && core && !this._areDepsSatisfied(core)) {
        result[i] = undefined;
      } else if (core != null) {
        result[i] = core;
      } else {
        objectsToLoad.push({ id: objectId, resultIndex: i });
      }
    }
    if (objectsToLoad.length === 0) {
      return result;
    }
    const idsToLoad = objectsToLoad.map((v) => v.id);
    this._loadObjectDocument(idsToLoad);

    const startTime = TRACE_LOADING ? performance.now() : 0;
    const diagnostics: string[] = [];
    let disposeCancellation: CleanupFn = () => {};
    try {
      return await new Promise((resolve, reject) => {
        let unsubscribe: CleanupFn | null = null;
        let inactivityTimeoutTimer: any | undefined;
        const scheduleInactivityTimeout = () => {
          inactivityTimeoutTimer = setTimeout(() => {
            unsubscribe?.();
            if (failOnTimeout) {
              diagnostics.push('inactivity-rejected');
              reject(new TimeoutError(inactivityTimeout));
            } else {
              diagnostics.push('inactivity-resolved');
              resolve(result);
            }
          }, inactivityTimeout);
        };
        unsubscribe = this._updateEvent.on(({ itemsUpdated }) => {
          const updatedIds = itemsUpdated.map((v) => v.id);
          for (let i = objectsToLoad.length - 1; i >= 0; i--) {
            const objectToLoad = objectsToLoad[i];
            if (updatedIds.includes(objectToLoad.id)) {
              clearTimeout(inactivityTimeoutTimer);

              const isDeleted = this._objects.get(objectToLoad.id)?.isDeleted();
              const depsUnsatisfied =
                this._objects.get(objectToLoad.id) && !this._areDepsSatisfied(this._objects.get(objectToLoad.id)!);

              if (!returnDeleted && isDeleted) {
                diagnostics.push('object-deleted');
                result[objectToLoad.resultIndex] = undefined;
              } else if (!returnWithUnsatisfiedDeps && depsUnsatisfied) {
                diagnostics.push('deps-unsatisfied');
                result[objectToLoad.resultIndex] = undefined;
              } else {
                result[objectToLoad.resultIndex] = this.getObjectCoreById(objectToLoad.id)!;
              }

              objectsToLoad.splice(i, 1);
              scheduleInactivityTimeout();
            }
          }
          if (objectsToLoad.length === 0) {
            clearTimeout(inactivityTimeoutTimer);
            unsubscribe?.();
            resolve(result);
          }
        });
        // The listener above outlives a close, and its updates stop arriving — without this the
        // batch would report an inactivity timeout rather than the cancellation that occurred.
        disposeCancellation = this.#lifetimeEnded.on(() => {
          clearTimeout(inactivityTimeoutTimer);
          unsubscribe?.();
          diagnostics.push('lifetime-ended');
          reject(new ContextDisposedError());
        });
        scheduleInactivityTimeout();
      });
    } finally {
      disposeCancellation();
      if (TRACE_LOADING) {
        log.info('loading objects', { objectIds, elapsed: performance.now() - startTime, diagnostics });
      }
    }
  }

  addCore(core: ObjectCore, opts?: AddCoreOptions): void {
    // One commit: live queries must see the object bound and available, not the link written before it.
    runInCommit(() => this.#addCore(core, opts));
  }

  #addCore(core: ObjectCore, opts?: AddCoreOptions): void {
    if (core.entityManager) {
      if (core.entityManager !== this) {
        throw new Error('Object already belongs to another database');
      }

      if (core.isDeleted()) {
        core.setDeleted(false);
      }

      return;
    }

    invariant(!this._objects.has(core.id));
    this._objects.set(core.id, core);

    let spaceDocHandle: DocHandleProxy<DatabaseDirectory>;
    const placement = opts?.placeIn ?? 'linked-doc';
    switch (placement) {
      case 'linked-doc': {
        spaceDocHandle = this._createDocumentForObject(core.id);
        spaceDocHandle.on('change', this._onDocumentUpdate);
        break;
      }
      case 'root-doc': {
        spaceDocHandle = this.getSpaceRootDocHandle();
        this._onObjectBoundToDocument(spaceDocHandle, core.id);
        break;
      }
      default:
        throw new TypeError(`Unknown object placement: ${placement}`);
    }

    core.bind({
      db: this,
      docHandle: spaceDocHandle,
      path: ['objects', core.id],
      assignFromLocalState: true,
    });

    this._markObjectAvailable(core.id);
  }

  removeCore(core: ObjectCore): void {
    invariant(this._objects.has(core.id));
    core.setDeleted(true);
  }

  unlinkObjects(objectIds: string[]): void {
    const root = this.getSpaceRootDocHandle();
    for (const objectId of objectIds) {
      if (!root.doc().links?.[objectId]) {
        throw new Error(`Link not found: ${objectId}`);
      }
    }
    root.change((doc) => {
      for (const objectId of objectIds) {
        delete doc.links![objectId];
      }
    });
  }

  /**
   * Replaces the set of objects the space directory tracks, dropping everything not retained.
   *
   * The drop set is derived from the directory's own maps rather than queried, so clearing a space
   * costs one root change instead of a scan of its contents. The dropped documents are orphaned,
   * which is what the host reclaims.
   *
   * @returns Ids of the objects dropped from the directory.
   */
  retainObjects(keep: Iterable<string>): string[] {
    // One commit: live queries are notified after the dropped objects are evicted, not between the write and the eviction.
    return runInCommit(() => this.#retainObjects(keep));
  }

  #retainObjects(keep: Iterable<string>): string[] {
    const root = this.getSpaceRootDocHandle();
    const doc = root.doc();
    const retained = new Set(keep);
    const droppedInline = Object.keys(doc.objects ?? {}).filter((id) => !retained.has(id));
    const droppedLinks = Object.keys(doc.links ?? {}).filter((id) => !retained.has(id));
    // A branch registry entry keeps its member documents reachable, so an entry outliving its root
    // object pins storage that nothing can reach.
    const droppedBranches = Object.keys(doc.branches ?? {}).filter((id) => !retained.has(id));
    const dropped = [...droppedInline, ...droppedLinks];
    if (dropped.length === 0 && droppedBranches.length === 0) {
      return [];
    }

    root.change((draft: DatabaseDirectory) => {
      for (const id of droppedInline) {
        delete draft.objects![id];
      }
      for (const id of droppedLinks) {
        delete draft.links![id];
      }
      for (const id of droppedBranches) {
        delete draft.branches![id];
      }
    });

    // Nothing re-derives a client's view of a space from the directory, so an object left here
    // would keep answering queries. Evicting only what was dropped above spares an object whose
    // document is still being created, which is bound before its link is written.
    for (const id of dropped) {
      this._objects.delete(id);
      this._releaseObject(id, { releaseDocument: true });
    }
    this._updateScheduler.forceTrigger();

    return dropped;
  }

  async unlinkDeletedObjects({ batchSize = 10 }: { batchSize?: number } = {}): Promise<void> {
    const idChunks = chunkArray(this.getAllObjectIds(), batchSize);
    for (const ids of idChunks) {
      const objects = await this.batchLoadObjectCores(ids, { returnDeleted: true });
      const toUnlink = objects.filter((o) => o?.isDeleted()).map((o) => o!.id);
      this.unlinkObjects(toUnlink);
    }
  }

  async atomicReplaceObject(id: EntityId, params: AtomicReplaceObjectProps): Promise<void> {
    const { data, type, meta } = params;

    const core = await this.loadObjectCoreById(id);
    invariant(core);

    const mappedData = deepMapValues(data, (value, recurse) => {
      if (Ref.isRef(value)) {
        return { '/': value.uri };
      }
      if (value instanceof Uint8Array) {
        return value;
      }
      return recurse(value);
    });
    delete mappedData.id;
    invariant(mappedData['@type'] === undefined);
    invariant(mappedData['@meta'] === undefined);

    const existingStruct: EntityStructure = deepMapValues(core.getDecoded([]), (value, recurse) =>
      value instanceof Uint8Array ? value : recurse(value),
    );
    const newStruct: EntityStructure = {
      ...existingStruct,
      data: mappedData,
    };

    if (type !== undefined) {
      newStruct.system!.type = EncodedReference.fromURI(type);
    }

    if (meta !== undefined) {
      newStruct.meta = { ...existingStruct.meta, ...meta };
    }

    core.setDecoded([], newStruct);
  }

  /** Whether a change or a new object exists only in this client, so losing the page would lose it. */
  hasUnsavedChanges(): boolean {
    return this._pendingDocumentCreations.size > 0 || this._repoProxy.hasUnsavedChanges();
  }

  async flush({
    disk = true,
    indexes = true,
    secondaryIndexes = false,
    updates = false,
  }: Database.FlushOptions = {}): Promise<void> {
    log('flush', { disk, indexes, secondaryIndexes, updates });
    const watermark = this.#writeSequence;
    await this._waitForPendingCreations();
    if (disk) {
      await this._repoProxy.flush({ disk: true });
    }

    if (indexes || secondaryIndexes) {
      await runServiceCall(this._runtime, this._dataService, 'DataService.updateIndexes', { secondaryIndexes });
      // Only a flush that pushed local changes to the host knows they reached the index.
      if (disk) {
        for (const [id, sequence] of this.#unindexedWrites) {
          if (sequence <= watermark) {
            this.#unindexedWrites.delete(id);
          }
        }
      }
    }

    if (updates) {
      await this._updateScheduler.runBlocking();
    }
  }

  async getDocumentHeads(): Promise<SpaceDocumentHeads> {
    const root = this.getSpaceRootDocHandle();
    const doc = root.doc();
    if (!doc || root.documentId == null) {
      return { heads: {} };
    }

    const headsStates = await runServiceCall(
      this._runtime,
      this._dataService,
      'DataService.getDocumentHeads',
      {
        documentIds: Object.values(doc.links ?? {}).map((link) => toDocumentId(link.toString() as AutomergeUrl)),
      },
      { timeout: RPC_TIMEOUT },
    );

    const heads: Record<string, string[]> = {};
    for (const state of headsStates.heads.entries ?? []) {
      heads[state.documentId] = state.heads ?? [];
    }

    heads[root.documentId] = getHeads(doc);

    return { heads };
  }

  async waitUntilHeadsReplicated(heads: SpaceDocumentHeads): Promise<void> {
    await runServiceCall(this._runtime, this._dataService, 'DataService.waitUntilHeadsReplicated', {
      heads: {
        entries: Object.entries(heads.heads).map(([documentId, heads]) => ({ documentId, heads })),
      },
    });

    await this._waitUntilRootDocHasHeads(heads);
  }

  /**
   * Waits for this client's replica of the space root document to carry the given heads.
   *
   * The service call is a host-side barrier only, but the root document carries the object ->
   * document routing table that index-hit hydration validates against
   * (`EchoClient._loadObjectFromDocument`), so a query run before this replica catches up drops the
   * hits it cannot route and comes back empty. Cf. dxos/dxos#7240.
   */
  private async _waitUntilRootDocHasHeads(heads: SpaceDocumentHeads): Promise<void> {
    const rootHandle = this._spaceRootDocHandle;
    const rootDocumentId = rootHandle?.documentId;
    if (!rootHandle || !rootDocumentId) {
      return;
    }
    const rootHeads = heads.heads[rootDocumentId];
    if (!rootHeads?.length) {
      return;
    }

    await asyncTimeout(
      Event.wrap<ChangeEvent<DatabaseDirectory>>(rootHandle, 'change').waitForCondition(() => {
        const doc = rootHandle.doc();
        return doc != null && A.hasHeads(doc, rootHeads);
      }),
      RPC_TIMEOUT,
      'waiting for the space root document to replicate to the client',
    );
  }

  async reIndexHeads(): Promise<void> {
    const root = this.getSpaceRootDocHandle();
    const doc = root.doc();
    invariant(doc);
    invariant(root.documentId, 'Space root document must have documentId');

    await runServiceCall(this._runtime, this._dataService, 'DataService.reIndexHeads', {
      documentIds: [
        root.documentId,
        ...Object.values(doc.links ?? {}).map((link) => toDocumentId(link as AutomergeUrl)),
      ],
    });
  }

  /** @deprecated Use `flush()`. */
  async updateIndexes(): Promise<void> {
    await runServiceCall(this._runtime, this._dataService, 'DataService.updateIndexes', {});
  }

  /** Host-side stats only; the client's own residency is added by {@link DatabaseImpl.stats}. */
  async stats(): Promise<DataService.DatabaseStats> {
    return runServiceCall(
      this._runtime,
      this._dataService,
      'DataService.stats',
      { spaceId: this.spaceId },
      { timeout: RPC_TIMEOUT },
    );
  }

  /** What this space holds in the client realm: proxied document handles and object cores. */
  loadedStats(): { documents: number; objects: number } {
    return {
      documents: Object.keys(this._repoProxy.handles).length,
      objects: this._objects.size,
    };
  }

  async runGarbageCollection(options?: Database.GarbageCollectionOptions): Promise<Database.GarbageCollectionReport> {
    return runServiceCall(
      this._runtime,
      this._dataService,
      'DataService.runGarbageCollection',
      {
        spaceId: this.spaceId,
        index: options?.index,
        feeds: options?.feeds,
      },
      { timeout: RPC_TIMEOUT },
    );
  }

  async getSyncState(): Promise<DataService.SpaceSyncState> {
    const value = await runServiceEffect(
      this._runtime,
      this._dataService['DataService.subscribeSpaceSyncState']({ spaceId: this.spaceId }).pipe(
        Stream.runHead,
        Effect.map(Option.getOrElse(() => raise(new EchoClientError({ message: 'Failed to get sync state.' })))),
      ),
      { timeout: RPC_TIMEOUT, label: 'DataService.subscribeSpaceSyncState' },
    );
    return value;
  }

  subscribeToSyncState(ctx: Context, callback: (state: DataService.SpaceSyncState) => void): CleanupFn {
    let cleanup: (() => void) | undefined;

    const setupStream = () => {
      cleanup = subscribeStream(
        this._runtime,
        this._dataService['DataService.subscribeSpaceSyncState']({ spaceId: this.spaceId }),
        {
          onData: (data) => {
            void runInContextAsync(ctx, () => callback(data));
          },
          onError: (err) => {
            if (err instanceof RpcClosedError) {
              this._reconnected.once(ctx, () => setupStream());
            } else if (err) {
              ctx.raise(err);
            }
          },
        },
      );
    };

    setupStream();
    ctx.onDispose(() => cleanup?.());
    return () => cleanup?.();
  }

  getAllObjectIds(): string[] {
    if (!this._spaceRootDocHandle) {
      return [];
    }

    const hasLoadedHandles = this._getAllDocHandles().length > 0;
    if (!hasLoadedHandles) {
      return [];
    }
    const rootDoc = this.getSpaceRootDocHandle().doc();
    if (!rootDoc) {
      return [];
    }

    return [...new Set([...Object.keys(rootDoc.objects ?? {}), ...Object.keys(rootDoc.links ?? {})])];
  }

  getNumberOfInlineObjects(): number {
    return Object.keys(this.getSpaceRootDocHandle().doc()?.objects ?? {}).length;
  }

  getNumberOfLinkedObjects(): number {
    return Object.keys(this.getSpaceRootDocHandle().doc()?.links ?? {}).length;
  }

  getTotalNumberOfObjects(): number {
    return this.getNumberOfInlineObjects() + this.getNumberOfLinkedObjects();
  }

  getLoadedDocumentHandles(): DocHandleProxy<any>[] {
    return Object.values(this._repoProxy.handles);
  }

  _updateServices({
    dataService,
    queryService,
  }: {
    dataService: DataService.Client;
    queryService: QueryService.Client;
  }): void {
    this._dataService = dataService;
    this._queryService = queryService;
    this._repoProxy._updateDataService(dataService);
  }

  async _onReconnect(): Promise<void> {
    log('re-establishing database streams');
    await this._repoProxy._onReconnect();
    this._reconnected.emit();
  }

  // ── Document-handle public surface ───────────────────────────────────────

  getSpaceRootDocHandle(): DocHandleProxy<DatabaseDirectory> {
    invariant(this._spaceRootDocHandle, 'Database was not initialized with root object.');
    return this._spaceRootDocHandle;
  }

  getLinkedDocHandles(): DocHandleProxy<DatabaseDirectory>[] {
    const rootHandle = this._spaceRootDocHandle;
    return [...new Set(this._objectDocumentHandles.values())].filter((h) => h !== rootHandle);
  }

  getObjectDocumentId(objectId: string): string | undefined {
    invariant(this._spaceRootDocHandle, 'Database was not initialized with root object.');
    const spaceRootDoc = this._spaceRootDocHandle.doc();
    invariant(spaceRootDoc);
    if (spaceRootDoc.objects?.[objectId]) {
      return this._spaceRootDocHandle.documentId;
    }
    const documentUrl = this._getLinkedDocumentUrl(objectId);
    return documentUrl && toDocumentId(documentUrl.toString() as AutomergeUrl);
  }

  //
  // Branching.
  // A branch is a writable alternate timeline of an object subtree: each member's document is forked
  // into a separate (synced) branch document; the space-root `branches` registry maps branch name ->
  // member doc url. The currently-selected branch is device-local (never synced).
  //

  /** The branch name this device currently views the object on; `'main'` (the object's main doc) by default. */
  getCurrentBranch(objectId: string): string {
    return this._currentBranches.get(objectId) ?? 'main';
  }

  /** The synced branch registry for a subtree-root object (undefined if it has no branches). */
  getBranchRegistry(rootObjectId: string): Record<string, BranchRecord> | undefined {
    return DatabaseDirectory.getBranches(this.getSpaceRootDocHandle().doc(), rootObjectId as EntityId);
  }

  /**
   * All branch names available for an object, including the implicit `'main'` (always first).
   * Works for a subtree root or any of its members (members inherit the root's branches).
   */
  listBranches(objectId: string): string[] {
    const rootId = this.getBranchRegistry(objectId) ? objectId : this._findBranchRootFor(objectId);
    return ['main', ...Object.keys((rootId && this.getBranchRegistry(rootId)) || {})];
  }

  /** The subtree root that owns the branch set containing `objectId`, if any. */
  private _findBranchRootFor(objectId: string): string | undefined {
    const branches = this.getSpaceRootDocHandle().doc().branches ?? {};
    for (const [rootId, byName] of Object.entries(branches)) {
      for (const record of Object.values(byName)) {
        if (record.members[objectId]) {
          return rootId;
        }
      }
    }
    return undefined;
  }

  /**
   * Fork the object and its referenced subtree into a new branch. Does not switch to it.
   *
   * Storage/replication cost: each member's branch doc is a full `A.save` copy (history included —
   * deliberately, so `A.merge` back into main is a true shared-ancestry CRDT merge), and branch
   * docs join the space document list via the root registry (see `DatabaseRoot.getAllLinkedDocuments`),
   * so they replicate and export like linked object docs. O(subtree size x branch count).
   *
   * @param opts.fromHeads Fork from a historical frontier instead of the tip. A `Heads` array forks
   *   only the root from that frontier (children fork at their tip); a `{ objectId -> Heads }` map
   *   forks each member from its own frontier (e.g. the scrubbed position across the whole subtree).
   */
  async createBranch(
    rootObjectId: string,
    name: string,
    opts?: { fromHeads?: Heads | Record<string, Heads> },
  ): Promise<void> {
    // Serialized with switch/merge/delete: a concurrent `switchBranch` between the awaited imports
    // could rebind members to another doc, forking one branch from mixed source documents.
    return this.#enqueueBranchOp(() => this.#createBranchInternal(rootObjectId, name, opts));
  }

  async #createBranchInternal(
    rootObjectId: string,
    name: string,
    opts?: { fromHeads?: Heads | Record<string, Heads> },
  ): Promise<void> {
    invariant(name !== 'main', "'main' is the implicit default branch");
    invariant(!this.getBranchRegistry(rootObjectId)?.[name], `branch already exists: ${name}`);
    const rootCore = this._objects.get(rootObjectId) ?? (await this.loadObjectCoreById(rootObjectId)) ?? undefined;
    invariant(rootCore, 'root object not found');

    // Normalize to a per-member frontier map; a bare `Heads` applies to the root only.
    const memberHeads: Record<string, Heads> = Array.isArray(opts?.fromHeads)
      ? { [rootObjectId]: opts.fromHeads }
      : (opts?.fromHeads ?? {});

    const members = await this._collectSubtree(rootCore);
    // Validate the whole subtree up front, before importing any documents: a member failing the
    // check mid-loop would otherwise leave already-imported branch docs orphaned in the repo. Fork
    // only from main — the source doc is the member's current core doc, so a subtree switched to
    // another branch would capture that branch's content (branch-of-branch is not yet supported).
    for (const member of members) {
      invariant(this._hasOwnDocument(member.id), 'cannot branch an inline object (promotion not yet implemented)');
      invariant(!this._currentBranches.has(member.id), 'create a branch from main only (switch to main first)');
    }

    const memberUrls: BranchRecord['members'] = {};
    for (const member of members) {
      // No `change` listener here: the object stays on its current branch; the branch doc only needs
      // to exist and replicate (referenced via the registry below). A listener is attached on switch.
      const handle = this._repoProxy.import<DatabaseDirectory>(forkDump(member.getDoc(), memberHeads[member.id]));
      await handle.whenReady();
      invariant(handle.url, 'branch document has no url');
      memberUrls[member.id] = handle.url;
    }

    const spaceRoot = this.getSpaceRootDocHandle();
    const baseHeads = memberHeads[rootObjectId] ?? getHeads(rootCore.getDoc());
    const createdAt = Date.now();
    spaceRoot.change((doc: DatabaseDirectory) => {
      // Assign through re-read doc proxies (not a chained `??=` result, which returns the orphan
      // literal under Automerge and silently drops the write).
      doc.branches ??= {};
      doc.branches[rootObjectId] ??= {};
      doc.branches[rootObjectId][name] = { members: memberUrls, baseHeads, createdAt };
    });
    this.branchesChanged.emit();
  }

  /**
   * Chains branch transitions (switch, merge, delete fallback) so concurrent operations cannot
   * interleave their per-member rebinds and leave a subtree bound to mixed branches.
   */
  #branchOpChain: Promise<void> = Promise.resolve();

  #enqueueBranchOp<T>(op: () => Promise<T>): Promise<T> {
    const result = this.#branchOpChain.then(op);
    this.#branchOpChain = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  /**
   * Switch the object's subtree to a branch (or back to `'main'`). Cascades to every subtree member
   * and rebinds each `ObjectCore` to that branch's document, so the object shows the branch
   * consistently regardless of how it is later accessed. The selection is device-local.
   * Transitions are serialized: concurrent switches apply one after another.
   */
  async switchBranch(rootObjectId: string, name: string): Promise<void> {
    return this.#enqueueBranchOp(() => this.#switchBranchInternal(rootObjectId, name));
  }

  async #switchBranchInternal(rootObjectId: string, name: string): Promise<void> {
    const registry = this.getBranchRegistry(rootObjectId);
    if (name !== 'main') {
      invariant(registry?.[name], `branch not found: ${name}`);
    }
    // Membership is consistent across branches, so the union covers every member to (re)bind.
    const memberIds = new Set<string>([rootObjectId]);
    for (const record of Object.values(registry ?? {})) {
      for (const id of Object.keys(record.members)) {
        memberIds.add(id);
      }
    }
    for (const memberId of memberIds) {
      await this._rebindMemberToBranch(memberId, name, registry);
      // A member absent from the selected branch's set is (correctly) rebound to main by
      // `_rebindMemberToBranch`; record it as such so `getCurrentBranch` matches the real binding
      // rather than the requested branch.
      const boundToBranch = name !== 'main' && registry?.[name]?.members[memberId] != null;
      if (boundToBranch) {
        this._currentBranches.set(memberId, name);
      } else {
        this._currentBranches.delete(memberId);
      }
    }
    this._persistCurrentBranches();
    this._scheduleThrottledDbUpdate([...memberIds]);
    this.branchesChanged.emit();
  }

  /**
   * Fold a branch's changes back into main across the subtree via `A.merge` (well-defined because
   * the branch shares fork ancestry with main). Then switch back to main; optionally delete.
   */
  async mergeBranch(rootObjectId: string, name: string, opts?: { deleteAfter?: boolean }): Promise<void> {
    invariant(name !== 'main', 'cannot merge main into itself');
    return this.#enqueueBranchOp(async () => {
      const record = this.getBranchRegistry(rootObjectId)?.[name];
      invariant(record, `branch not found: ${name}`);
      // Preflight every member's branch doc + main handle before mutating any of them, so a member
      // that fails to load aborts the merge cleanly rather than leaving the subtree half-merged.
      const merges: Array<{ branchDoc: A.Doc<DatabaseDirectory>; mainHandle: DocHandleProxy<DatabaseDirectory> }> = [];
      for (const [memberId, urlData] of Object.entries(record.members)) {
        const branchHandle = this._repoProxy.find<DatabaseDirectory>(urlData.toString() as DocumentId);
        await branchHandle.whenReady();
        const mainHandle = await this._mainDocHandle(memberId);
        merges.push({ branchDoc: branchHandle.doc(), mainHandle });
      }
      for (const { branchDoc, mainHandle } of merges) {
        mainHandle.update((doc) => A.merge(doc, branchDoc));
      }
      await this.#switchBranchInternal(rootObjectId, 'main');
      if (opts?.deleteAfter) {
        this.deleteBranch(rootObjectId, name);
      }
    });
  }

  /**
   * Fold MAIN's changes into a branch across the subtree via `A.merge` (well-defined because the
   * branch shares fork ancestry with main), leaving the device on its current selection. The reverse
   * of {@link mergeBranch}: after syncing, the branch is "main + the branch's own changes", so a diff
   * of branch vs main contains only the branch's edits — main's progress never reads as deletions.
   */
  async syncBranch(rootObjectId: string, name: string): Promise<void> {
    invariant(name !== 'main', 'cannot sync main into itself');
    return this.#enqueueBranchOp(async () => {
      const record = this.getBranchRegistry(rootObjectId)?.[name];
      invariant(record, `branch not found: ${name}`);
      // Preflight every member before mutating any, so a failed load aborts cleanly.
      const merges: Array<{ branchHandle: DocHandleProxy<DatabaseDirectory>; mainDoc: A.Doc<DatabaseDirectory> }> = [];
      for (const [memberId, urlData] of Object.entries(record.members)) {
        const branchHandle = this._repoProxy.find<DatabaseDirectory>(urlData.toString() as DocumentId);
        await branchHandle.whenReady();
        const mainHandle = await this._mainDocHandle(memberId);
        merges.push({ branchHandle, mainDoc: mainHandle.doc() });
      }
      for (const { branchHandle, mainDoc } of merges) {
        branchHandle.update((doc) => A.merge(doc, mainDoc));
      }
      this._scheduleThrottledDbUpdate(Object.keys(record.members));
    });
  }

  /** Remove a branch from the registry (its documents lose their sync reference). Cannot delete main. */
  deleteBranch(rootObjectId: string, name: string): void {
    invariant(name !== 'main', 'cannot delete the main branch');
    const spaceRoot = this.getSpaceRootDocHandle();
    const memberIds = Object.keys(this.getBranchRegistry(rootObjectId)?.[name]?.members ?? {});
    spaceRoot.change((doc: DatabaseDirectory) => {
      if (doc.branches?.[rootObjectId]) {
        delete doc.branches[rootObjectId][name];
        if (Object.keys(doc.branches[rootObjectId]).length === 0) {
          delete doc.branches[rootObjectId];
        }
      }
    });
    // Members currently viewing the deleted branch fall back to main. The member list was
    // captured before the registry entry was removed (a post-deletion switch could no longer
    // enumerate the deleted branch's membership), and the rebind is serialized with other
    // branch transitions.
    const orphaned = memberIds.filter((id) => this._currentBranches.get(id) === name);
    if (orphaned.length > 0) {
      this.#enqueueBranchOp(async () => {
        for (const memberId of orphaned) {
          await this._rebindMemberToBranch(memberId, 'main', undefined);
          this._currentBranches.delete(memberId);
        }
        this._persistCurrentBranches();
        this._scheduleThrottledDbUpdate(orphaned);
      }).catch((err) => log.warn('failed to fall back to main after branch deletion', { err }));
    }
    this.branchesChanged.emit();
  }

  /** BFS the object's referenced subtree (loading members as needed). */
  private async _collectSubtree(rootCore: ObjectCore): Promise<ObjectCore[]> {
    const seen = new Set<string>();
    const result: ObjectCore[] = [];
    const queue: ObjectCore[] = [rootCore];
    while (queue.length > 0) {
      const core = queue.shift()!;
      if (seen.has(core.id)) {
        continue;
      }
      seen.add(core.id);
      result.push(core);
      const structure = core.getObjectStructure();
      for (const id of structure ? referencedObjectIds(structure) : []) {
        if (seen.has(id)) {
          continue;
        }
        const child = this._objects.get(id) ?? (await this.loadObjectCoreById(id as EntityId)) ?? undefined;
        if (child) {
          queue.push(child);
        }
      }
    }
    return result;
  }

  /** Whether the object has its own linked document (not inlined in the space root). */
  private _hasOwnDocument(objectId: string): boolean {
    return this.getSpaceRootDocHandle().doc().links?.[objectId] != null;
  }

  /** Resolve the object's main (default-branch) document handle. */
  private async _mainDocHandle(objectId: string): Promise<DocHandleProxy<DatabaseDirectory>> {
    const spaceRoot = this.getSpaceRootDocHandle();
    const url = spaceRoot.doc().links?.[objectId]?.toString();
    if (!url) {
      return spaceRoot; // Inline object.
    }
    const handle = this._repoProxy.find<DatabaseDirectory>(url as DocumentId);
    await handle.whenReady();
    return handle;
  }

  /**
   * Core-level independent-instance branch binding: an ephemeral `ObjectCore` bound to the branch document
   * of one object, independent of the device-global selection (`switchBranch`) and of other
   * bindings. The caller owns the returned core and must call `dispose` — bindings are never
   * persisted (the {@link BranchStore} records only the device default, which bindings override
   * locally and never mutate). Reads resolve the branch doc; writes land on the branch doc only.
   */
  async bindCoreToBranch(objectId: string, name: string): Promise<{ core: ObjectCore; dispose: () => void }> {
    invariant(name !== 'main', "binding to 'main' resolves the live object; no core binding needed");
    const rootId = this.getBranchRegistry(objectId) ? objectId : this._findBranchRootFor(objectId);
    const record = rootId ? this.getBranchRegistry(rootId)?.[name] : undefined;
    invariant(record, `branch not found: ${name}`);
    const url = record.members[objectId]?.toString();
    invariant(url, `object is not a member of branch: ${name}`);
    const handle = this._repoProxy.find<DatabaseDirectory>(url as DocumentId);
    await handle.whenReady();

    const core = new ObjectCore();
    core.id = objectId as EntityId;
    core.bind({ db: this, docHandle: handle, path: ['objects', objectId], assignFromLocalState: false });
    // This independent instance is bound to `name`; `Obj.getBranch` reads it off the core.
    core.branch = name;

    // Route branch-doc changes to this binding's core directly: the id-keyed `_objects` routing
    // (`_onDocumentUpdate` -> `_emitObjectUpdateEvent`) serves the device-global binding only, and
    // this handle may not carry `_onDocumentUpdate` at all while the device views another branch.
    const onChange = (event: ChangeEvent<DatabaseDirectory>) => {
      if (event.patches.some((patch) => patch.path[0] === 'objects' && patch.path[1] === objectId)) {
        core.notifyUpdate();
      }
    };
    handle.on('change', onChange);

    return {
      core,
      dispose: () => {
        // Releases the binding's listeners only — the branch document itself stays (it is referenced
        // by the synced registry and keeps replicating).
        handle.off('change', onChange);
        for (const unsubscribe of core.subscriptions) {
          unsubscribe();
        }
      },
    };
  }

  private async _rebindMemberToBranch(
    memberId: string,
    name: string,
    registry: Record<string, BranchRecord> | undefined,
  ): Promise<void> {
    const core = this._objects.get(memberId) ?? (await this.loadObjectCoreById(memberId as EntityId)) ?? undefined;
    if (!core) {
      return;
    }
    const url = name !== 'main' ? registry?.[name]?.members[memberId]?.toString() : undefined;
    if (!url && core.snapshot) {
      return; // A snapshot core shows its main document.
    }
    let handle: DocHandleProxy<DatabaseDirectory>;
    if (url) {
      handle = this._repoProxy.find<DatabaseDirectory>(url as DocumentId);
      await handle.whenReady();
    } else {
      // 'main', or a member absent from this branch's set, binds to its main document.
      handle = await this._mainDocHandle(memberId);
    }
    if (handle === core.docHandle) {
      return; // Already bound to the target doc.
    }
    // Move the change listener to the newly-bound doc. `_processDocumentUpdate` rebinds an object to
    // whichever doc fires a change, so a non-current branch doc holding the same object id must NOT
    // carry the listener. The space root (shared by inline objects) keeps its listener.
    const spaceRoot = this.getSpaceRootDocHandle();
    if (core.docHandle && core.docHandle !== spaceRoot) {
      core.docHandle.off('change', this._onDocumentUpdate);
    }
    if (handle !== spaceRoot) {
      handle.off('change', this._onDocumentUpdate);
      handle.on('change', this._onDocumentUpdate);
    }
    core.bind({ db: this, docHandle: handle, path: ['objects', memberId], assignFromLocalState: false });
    // The canonical instance now views this branch; `Obj.getBranch` reads it. A member absent from the
    // branch's set was bound to main above (`url` undefined), so it reports `'main'`, not `name`.
    core.branch = url ? name : 'main';
    this._onObjectBoundToDocument(handle, memberId);
  }

  // ── Document-handle private implementation ───────────────────────────────

  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  private async _loadSpaceRootDocHandle(ctx: Context, spaceState: SpaceState): Promise<void> {
    if (this._spaceRootDocHandle != null) {
      return;
    }
    if (!spaceState.rootUrl) {
      throw new Error('Database opened with no rootUrl');
    }

    const existingDocHandle = await this._initDocHandle(ctx, spaceState.rootUrl).catch((err) => {
      // Named here because the caller only knows it failed to open the space: every read and write
      // in it fails, and which document is missing is the whole diagnosis.
      throw DocumentUnavailableError.is(err)
        ? new EchoClientError({
            message: 'Space root document is not available.',
            context: { spaceId: this._spaceId, rootUrl: spaceState.rootUrl },
            cause: err,
          })
        : err;
    });
    const doc = existingDocHandle.doc();
    invariant(doc);
    invariant(doc.version === SpaceDocVersion.CURRENT);
    if (doc.access?.spaceId == null || doc.access?.spaceKey == null) {
      this._initDocAccess(existingDocHandle);
    }
    this._spaceRootDocHandle = existingDocHandle;
  }

  private _objectPresent(id: EntityId): boolean {
    assertState(this._spaceRootDocHandle, 'Database was not initialized with root object.');
    return (
      DatabaseDirectory.getInlineObject(this._spaceRootDocHandle.doc(), id) != null ||
      DatabaseDirectory.getLink(this._spaceRootDocHandle.doc(), id) != null
    );
  }

  private _loadObjectDocument(objectIdOrMany: string | string[], opts: LoadObjectDocumentOptions = {}): void {
    const objectIds = Array.isArray(objectIdOrMany) ? objectIdOrMany : [objectIdOrMany];
    let hasUrlsToLoad = false;
    const urlsToLoad: DatabaseDirectory['links'] = {};
    for (const objectId of objectIds) {
      invariant(this._spaceRootDocHandle, 'Database was not initialized with root object.');
      if (
        this._objectDocumentHandles.has(objectId) ||
        this._objectsPendingDocumentLoad.has(objectId) ||
        this._objects.get(objectId)?.snapshot
      ) {
        continue;
      }
      const documentUrl = this._getLinkedDocumentUrl(objectId);
      if (documentUrl == null) {
        this._objectsPendingDocumentLoad.set(objectId, opts);
        const isInline = DatabaseDirectory.getInlineObject(this._spaceRootDocHandle.doc(), objectId) != null;
        if (!isInline) {
          log('object absent from space directory, marking unavailable', { objectId });
          this._onObjectUnavailable({ objectId });
        } else {
          log('loading delayed until object links are initialized', { objectId });
        }
      } else {
        urlsToLoad[objectId] = documentUrl;
        hasUrlsToLoad = true;
      }
    }
    if (hasUrlsToLoad) {
      this._loadLinkedObjects(urlsToLoad, opts);
    }
  }

  private _onObjectLinksUpdated(links: SpaceDocumentLinks): void {
    if (!links) {
      return;
    }
    // A reader that could not route an object to its document — an index hit, say — can once these
    // arrive.
    const added = Object.keys(links).filter((objectId) => !this.#linkedObjectIds.has(objectId));
    if (added.length > 0) {
      added.forEach((objectId) => this.#linkedObjectIds.add(objectId));
      this._linksAddedEvent.emit(added);
    }
    const linksAwaitingLoad = Object.entries(links).filter(([objectId]) =>
      this._objectsPendingDocumentLoad.has(objectId),
    );
    if (linksAwaitingLoad.length > 0) {
      const groups = new Map<boolean, typeof linksAwaitingLoad>();
      for (const entry of linksAwaitingLoad) {
        const opts = this._objectsPendingDocumentLoad.get(entry[0]) ?? {};
        const key = !!opts.diskOnly;
        const bucket = groups.get(key) ?? [];
        bucket.push(entry);
        groups.set(key, bucket);
      }
      for (const [diskOnly, entries] of groups) {
        this._loadLinkedObjects(Object.fromEntries(entries), { diskOnly });
      }
    }
    linksAwaitingLoad.forEach(([objectId]) => this._objectsPendingDocumentLoad.delete(objectId));
  }

  private _onObjectBoundToDocument(handle: DocHandleProxy<DatabaseDirectory>, objectId: string): void {
    this._bindObjectDocument(objectId, handle);
  }

  /** Records the object -> document mapping in both directions. */
  private _bindObjectDocument(objectId: string, handle: DocHandleProxy<DatabaseDirectory>): void {
    const previous = this._objectDocumentHandles.get(objectId);
    if (previous !== undefined && previous !== handle) {
      this._forgetDocumentObject(objectId, previous);
    }
    this._objectDocumentHandles.set(objectId, handle);
    // Keyed by the handle itself, not its documentId: a locally created document has no id until the
    // host assigns one, and an entry keyed on `undefined` would make unrelated objects look like
    // they share a document. The map only ever holds handles with at least one loaded object, so it
    // is not itself a reason for a handle to stay resident.
    const objects = this._documentObjects.get(handle) ?? new Set<string>();
    objects.add(objectId);
    this._documentObjects.set(handle, objects);
  }

  /** Drops the object -> document mapping, returning the handle it was bound to. */
  private _unbindObjectDocument(objectId: string): DocHandleProxy<DatabaseDirectory> | undefined {
    const handle = this._objectDocumentHandles.get(objectId);
    this._objectDocumentHandles.delete(objectId);
    if (handle !== undefined) {
      this._forgetDocumentObject(objectId, handle);
    }
    return handle;
  }

  private _forgetDocumentObject(objectId: string, handle: DocHandleProxy<DatabaseDirectory>): void {
    const objects = this._documentObjects.get(handle);
    if (objects === undefined) {
      return;
    }
    objects.delete(objectId);
    if (objects.size === 0) {
      this._documentObjects.delete(handle);
    }
  }

  private _createDocumentForObject(objectId: string): DocHandleProxy<DatabaseDirectory> {
    invariant(this._spaceRootDocHandle, 'Database was not initialized with root object.');
    const spaceDocHandle = this._repoProxy.create<DatabaseDirectory>({
      version: SpaceDocVersion.CURRENT,
      // spaceKey is deprecated but still written so older clients can resolve the owning space.
      access: { spaceId: this._spaceId, spaceKey: this._spaceKey.toHex() },
    });
    const creationPromise = spaceDocHandle
      .whenReady()
      .then(() => {
        if (this._spaceRootDocHandle == null) {
          log.warn('space root document handle is not available, skipping object binding', { objectId });
          return;
        }
        const url = spaceDocHandle.url;
        if (url == null) {
          log.warn('document has no url after whenReady, skipping object binding', { objectId });
          return;
        }
        this._spaceRootDocHandle.change((newDoc: DatabaseDirectory) => {
          newDoc.links ??= {};
          newDoc.links[objectId] = new A.RawString(url);
        });
      })
      .catch((error: unknown) => {
        log('object not bound: the database closed before its document was created', { objectId, err: error });
      })
      .finally(() => {
        this._pendingDocumentCreations.delete(objectId);
      });
    this._pendingDocumentCreations.set(objectId, creationPromise);
    this._onObjectBoundToDocument(spaceDocHandle, objectId);

    return spaceDocHandle;
  }

  /** Throws if a document could not be created, since its object would otherwise never reach the host. */
  private async _waitForPendingCreations(): Promise<void> {
    await this._repoProxy.flushCreations();
    await Promise.all([...this._pendingDocumentCreations.values()]);
  }

  private _clearHandleReferences(): string[] {
    const objectsWithHandles = [...this._objectDocumentHandles.keys()];
    this._objectDocumentHandles.clear();
    this.#linkedObjectIds.clear();
    this._documentObjects.clear();
    this._spaceRootDocHandle = null;
    return objectsWithHandles;
  }

  private _getAllDocHandles(): DocHandleProxy<DatabaseDirectory>[] {
    return this._spaceRootDocHandle != null
      ? [this._spaceRootDocHandle, ...new Set(this._objectDocumentHandles.values())]
      : [];
  }

  private _getLinkedDocumentUrl(objectId: string): AutomergeUrl | undefined {
    const spaceRootDoc = this._spaceRootDocHandle?.doc();
    invariant(spaceRootDoc);
    return (spaceRootDoc.links ?? {})[objectId]?.toString() as AutomergeUrl;
  }

  private _loadLinkedObjects(links: SpaceDocumentLinks, opts: LoadObjectDocumentOptions = {}): void {
    if (!links) {
      return;
    }
    if (this.#closed) {
      log('database closed while links were resolving, abandoning load', { links: Object.keys(links) });
      return;
    }
    for (const [objectId, automergeUrlData] of Object.entries(links)) {
      const automergeUrl = automergeUrlData.toString();
      const logMeta = { objectId, automergeUrl };
      const objectDocumentHandle = this._objectDocumentHandles.get(objectId);
      if (objectDocumentHandle?.url != null && objectDocumentHandle.url !== automergeUrl) {
        log.warn('object already inlined in a different document, ignoring the link', {
          ...logMeta,
          actualDocumentUrl: objectDocumentHandle.url,
        });
        continue;
      }
      if (objectDocumentHandle?.url === automergeUrl) {
        log.warn('object document was already loaded', logMeta);
        continue;
      }
      let handle: DocHandleProxy<DatabaseDirectory>;
      try {
        handle = this._repoProxy.find<DatabaseDirectory>(automergeUrl as DocumentId);
      } catch (err) {
        if (!RepoClosedError.is(err)) {
          throw err;
        }
        // The proxy closed under a load this manager started while open — its own `#closed` flag
        // tracks a different object's lifetime, so it cannot stand in for this check.
        log('repo closed while links were resolving, abandoning load', logMeta);
        return;
      }
      log.debug('document loading triggered', logMeta);
      this._bindObjectDocument(objectId, handle);
      void this._loadHandleForObject(handle, objectId, opts);
    }
  }

  private async _initDocHandle(ctx: Context, url: string): Promise<DocHandleProxy<DatabaseDirectory>> {
    const docHandle = this._repoProxy.find<DatabaseDirectory>(url as DocumentId);
    await warnAfterTimeout(5_000, 'Automerge root doc load timeout (EntityManager)', async () => {
      await cancelWithContext(ctx, docHandle.whenReady());
    });

    return docHandle;
  }

  private _initDocAccess(handle: DocHandleProxy<DatabaseDirectory>): void {
    handle.change((newDoc: DatabaseDirectory) => {
      newDoc.access ??= {};
      newDoc.access.spaceId = this._spaceId;
      // spaceKey is deprecated but still written so older clients can resolve the owning space.
      newDoc.access.spaceKey = this._spaceKey.toHex();
    });
  }

  /**
   * Creates the object's core once a document the host could not produce is finally delivered.
   *
   * The object stays bound to this handle, so every load path short-circuits on it and nothing
   * would notice the delivery; the handle's `available` event is the only signal, since the waiter
   * that would otherwise carry it is holding a rejected `whenReady`.
   */
  #recoverWhenAvailable(handle: DocHandleProxy<DatabaseDirectory>, objectId: string, generation: number): void {
    handle.once('available', () => {
      // The wait outlives the space that started it, so a handle re-bound elsewhere — or a closed
      // manager — must not recreate an evicted object.
      if (this.#isStale(generation) || this._objectDocumentHandles.get(objectId) !== handle) {
        return;
      }
      this._onObjectDocumentLoaded({ handle, objectId });
    });
  }

  /**
   * Rebinds an object to the document a root update pointed it at, once the host can produce it.
   *
   * A rebind onto an unavailable document is skipped rather than awaited, so the object keeps its
   * previous binding and nothing else would revisit the link.
   */
  #rebindWhenAvailable(handle: DocHandleProxy<DatabaseDirectory>, objectId: string, generation: number): void {
    handle.once('available', () => {
      if (this.#isStale(generation) || !this._objects.has(objectId)) {
        return;
      }
      // The directory may have re-pointed the object in the meantime; binding it to a superseded
      // document would restore content the space no longer links.
      if (this._getLinkedDocumentUrl(objectId) !== handle.url) {
        return;
      }
      this._rebindObjects(handle, [objectId]);
      this._markObjectAvailable(objectId);
    });
  }

  private async _loadHandleForObject(
    handle: DocHandleProxy<DatabaseDirectory>,
    objectId: string,
    opts: LoadObjectDocumentOptions = {},
  ): Promise<void> {
    invariant(handle.url, 'Document URL is not available');
    const generation = this.#generation;
    try {
      if (this._currentlyLoadingObjects.has({ url: handle.url, objectId })) {
        log.verbose('document is already loading', { objectId });
        return;
      }
      this._currentlyLoadingObjects.add({ url: handle.url, objectId });

      if (opts.diskOnly) {
        const onDisk = await handle.whenSettledOnDisk();
        if (this.#isStale(generation)) {
          this._currentlyLoadingObjects.delete({ url: handle.url, objectId });
          log('lifetime ended while a document settled on disk, abandoning load', { objectId });
          return;
        }
        if (!onDisk) {
          this._currentlyLoadingObjects.delete({ url: handle.url, objectId });
          log('object document unavailable on disk', { objectId, docUrl: handle.url });
          this._onObjectUnavailable({ handle, objectId });
          this.#recoverWhenAvailable(handle, objectId, generation);
          handle
            .whenReady()
            .then(() => {
              // This wait is detached and unbounded, so it routinely outlives the space that
              // started it; binding it to the lifetime keeps it from recreating an evicted object.
              if (this.#isStale(generation) || this._objectDocumentHandles.get(objectId) !== handle) {
                return;
              }
              this._onObjectDocumentLoaded({ handle, objectId });
            })
            // Already rejected where the host reported the document unavailable; the recovery
            // armed above is what carries that case.
            .catch((err) => log.verbose('background network wait failed', { objectId, err }));
          return;
        }
      }

      await handle.whenReady();
      this._currentlyLoadingObjects.delete({ url: handle.url, objectId });
      if (this.#isStale(generation)) {
        log('lifetime ended while a document was loading, abandoning load', { objectId });
        return;
      }

      const logMeta = { objectId, docUrl: handle.url };
      const objectDocHandle = this._objectDocumentHandles.get(objectId);
      if (objectDocHandle?.url != null && objectDocHandle.url !== handle.url) {
        log.warn('object was rebound while a document was loading, discarding handle', logMeta);
        return;
      }
      this._onObjectDocumentLoaded({ handle, objectId });
    } catch (err) {
      this._currentlyLoadingObjects.delete({ url: handle.url, objectId });
      if (this.#isStale(generation)) {
        log('lifetime ended while a document was loading, abandoning load', { objectId, err });
        return;
      }
      if (DocumentUnavailableError.is(err)) {
        // Terminal for now, so the retry below would spin: the host has said it cannot produce this
        // document. Recovery is armed rather than retried — the object stays bound to this handle,
        // so nothing would start a fresh load once the bytes do arrive.
        log.warn('object document is not available on the host', { objectId, automergeUrl: handle.url });
        this._onObjectUnavailable({ handle, objectId });
        this.#recoverWhenAvailable(handle, objectId, generation);
        return;
      }
      log.warn('failed to load a document, retrying', {
        objectId,
        automergeUrl: handle.url,
        err,
      });
      await this._loadHandleForObject(handle, objectId, opts);
    }
  }

  // ── Private document change handlers ────────────────────────────────────

  private async _handleSpaceRootDocumentChange(
    spaceRootDocHandle: DocHandleProxy<DatabaseDirectory>,
    objectsToLoad: string[],
  ): Promise<void> {
    const spaceRootUrl = spaceRootDocHandle.url;
    if (spaceRootUrl == null) {
      log.warn('space root document has no url');
      return;
    }

    const spaceRootDoc: DatabaseDirectory = spaceRootDocHandle.doc();
    const inlinedObjectIds = new Set(Object.keys(spaceRootDoc.objects ?? {}));
    const linkedObjectIds = new Map(Object.entries(spaceRootDoc.links ?? {}).map(([k, v]) => [k, v.toString()]));

    const objectsToRebind = new Map<string, { handle: DocHandleProxy<DatabaseDirectory>; objectIds: string[] }>();
    objectsToRebind.set(spaceRootUrl, { handle: spaceRootDocHandle, objectIds: [] });

    const objectsToRemove: string[] = [];
    const objectsToCreate = [...inlinedObjectIds.values()].filter((oid) => !this._objects.has(oid));

    for (const object of this._objects.values()) {
      if (inlinedObjectIds.has(object.id)) {
        if (object.handle?.url != null && object.handle.url === spaceRootUrl) {
          continue;
        }
        objectsToRebind.get(spaceRootUrl)!.objectIds.push(object.id);
      } else if (linkedObjectIds.has(object.id)) {
        const newObjectDocUrl = linkedObjectIds.get(object.id)!;
        if (object.handle?.url != null && object.handle.url === newObjectDocUrl) {
          continue;
        }
        const existing = objectsToRebind.get(newObjectDocUrl.toString());
        if (existing != null) {
          existing.objectIds.push(object.id);
          continue;
        }
        let newDocHandle: DocHandleProxy<DatabaseDirectory>;
        try {
          newDocHandle = this._repoProxy.find<DatabaseDirectory>(newObjectDocUrl as DocumentId);
        } catch (err) {
          if (!RepoClosedError.is(err)) {
            throw err;
          }
          // A root update delivered into teardown: no rebind can land, and the remaining objects
          // would each fail the same way.
          log('repo closed while rebinding objects, abandoning root update', { objectId: object.id });
          return;
        }
        try {
          await newDocHandle.whenReady();
        } catch (err) {
          if (!DocumentUnavailableError.is(err)) {
            throw err;
          }
          // One object the host cannot produce must not abandon the rest of the rebind, which is
          // what stops the space's remaining objects from ever seeing this root update.
          log.warn('object document is not available on the host, skipping rebind', {
            objectId: object.id,
            automergeUrl: newObjectDocUrl.toString(),
          });
          this._onObjectUnavailable({ objectId: object.id });
          // Skipping leaves the object on its previous document, so without this the replacement is
          // picked up only by whatever root update happens to come next.
          this.#rebindWhenAvailable(newDocHandle, object.id, this.#generation);
          continue;
        }
        newDocHandle.doc();
        objectsToRebind.set(newObjectDocUrl.toString(), { handle: newDocHandle, objectIds: [object.id] });
      } else {
        objectsToRemove.push(object.id);
      }
    }

    for (const oid of objectsToRemove) {
      this.#unwatchPassive(oid, { released: true });
      this._objects.delete(oid);
    }
    this._createInlineObjects(spaceRootDocHandle, objectsToCreate);
    for (const { handle, objectIds } of objectsToRebind.values()) {
      this._rebindObjects(handle, objectIds);
    }
    for (const objectId of objectsToLoad) {
      if (!this._objects.has(objectId)) {
        this._loadObjectDocument(objectId);
      }
    }
    this._onObjectLinksUpdated(spaceRootDoc.links);
    this._rootChangedEvent.emit();
  }

  private _emitObjectUpdateEvent(itemsUpdated: string[]): void {
    if (itemsUpdated.length === 0) {
      return;
    }

    batchEvents(() => {
      for (const id of itemsUpdated) {
        const objCore = this._objects.get(id);
        if (objCore) {
          objCore.notifyUpdate();
        }
      }
    });
  }

  private readonly _onDocumentUpdate = (event: ChangeEvent<DatabaseDirectory>) => {
    this._evictRemovedObjects(event);
    const documentChanges = this._processDocumentUpdate(event);
    const now = performance.now();
    const edited = event.patchInfo.source === 'change';
    for (const objectId of documentChanges.updatedObjectIds) {
      const core = this._objects.get(objectId);
      if (core) {
        core.activeAt = now;
        if (edited) {
          core.editedAt = now;
        }
      }
    }
    this._rebindObjects(event.handle, documentChanges.objectsToRebind);
    this._onObjectLinksUpdated(documentChanges.linkedDocuments);
    this._createInlineObjects(event.handle, documentChanges.createdObjectIds);
    this._markBodiesAvailable(documentChanges.updatedObjectIds);
    this._emitObjectUpdateEvent(documentChanges.updatedObjectIds);
    // A local write reaches live queries before the writing call returns; host deliveries keep the scheduler,
    // in a separate set so a local commit neither re-labels nor hurries the host updates waiting to coalesce.
    if (event.patchInfo.source === 'change') {
      for (const id of documentChanges.updatedObjectIds) {
        this.#objectsForLocalCommit.add(id);
        this.#unindexedWrites.set(id, ++this.#writeSequence);
      }
      afterCommit(this.#flushLocalCommit);
    } else {
      this._scheduleThrottledDbUpdate(documentChanges.updatedObjectIds, {
        coalesce: event.patchInfo.source === 'bulk',
      });
    }
  };

  /**
   * Emits the db update a local commit produced, once that commit has ended. Object-level notifications
   * queued by other paths (satisfaction, availability) stay on the scheduler: the written objects already
   * notified inside the commit, and anything else is not this commit's to deliver early.
   */
  readonly #flushLocalCommit = (): void => {
    if (this.#closed || this.#objectsForLocalCommit.size === 0) {
      return;
    }
    const ids = [...this.#objectsForLocalCommit];
    this.#objectsForLocalCommit.clear();
    // Mid root-document swap there is nothing to emit against; the scheduler re-emits after the swap.
    if (!this._spaceRootDocHandle) {
      this._scheduleThrottledDbUpdate(ids);
      return;
    }
    const event: ItemsUpdatedEvent = { spaceId: this.spaceId, itemsUpdated: ids.map((id) => ({ id })) };
    this._localCommitEvent.emit(event);
    batchEvents(() => this._updateEvent.emit(event));
  };

  /**
   * Drops objects whose directory entry was removed — a garbage-collection pass replicating in, or
   * a local {@link retainObjects}. Nothing else re-derives the working set from the directory, so an
   * object left here keeps answering queries long after its document is gone. An object whose
   * document is still being created is momentarily absent from the directory, so it is skipped.
   */
  private _evictRemovedObjects(event: ChangeEvent<DatabaseDirectory>): void {
    // Bound documents count, not just live cores: a core collected before its entry was removed
    // leaves the document behind, and `_rehydrateCore` would resurrect an object the directory no
    // longer lists from it.
    const removed = getRemovedObjectIds(event).filter(
      (objectId) =>
        (this._objects.has(objectId) || this._objectDocumentHandles.has(objectId)) &&
        !this._pendingDocumentCreations.has(objectId),
    );
    if (removed.length === 0) {
      return;
    }

    for (const objectId of removed) {
      this._objects.delete(objectId);
      this._releaseObject(objectId, { releaseDocument: true });
    }
    log('evicted objects removed from the space directory', { count: removed.length });
    this._updateScheduler.forceTrigger();
  }

  /**
   * Drops everything this space keeps beside a core once that core is gone — because the caller let
   * go of the object and it was collected, or because its directory entry was removed.
   *
   * `releaseDocument` separates the two: an unlinked object's document is dropped too (with the last
   * object mounted in it, and never the space root), since it is the document that holds the payload.
   */
  private _releaseObject(objectId: string, { releaseDocument = false }: ReleaseObjectOptions = {}): void {
    this.#unwatchPassive(objectId, { released: true });
    // Never dropped while still resolving, because aborting one releases its load ops and cancels
    // the IO a reader is waiting on; it is dropped when it settles instead.
    const request = this._satisfactionRequests.get(objectId as EntityId);
    if (request != null) {
      if (isSettled(request)) {
        this._dropSatisfactionRequest(objectId);
      } else {
        this._dropSatisfactionRequestWhenSettled(objectId, request);
      }
    }

    if (!releaseDocument) {
      // The core was transient; its document is not. A collected core says nothing about whether the
      // document is still being read — another object may share it, a load may be in flight — so the
      // document stays and the next read rebuilds the core from it.
      return;
    }

    this._objectsPendingDocumentLoad.delete(objectId);
    const handle = this._unbindObjectDocument(objectId);
    if (handle == null || handle === this._spaceRootDocHandle || handle.documentId == null) {
      return;
    }
    // In-flight load bookkeeping is keyed by document url, which a re-load reuses: an entry left
    // behind would make the next read report "already loading" against a handle that is gone.
    if (handle.url != null) {
      this._currentlyLoadingObjects.delete({ url: handle.url, objectId });
    }
    if ((this._documentObjects.get(handle)?.size ?? 0) > 0 || this._pendingDocumentCreations.has(objectId)) {
      return;
    }
    this._repoProxy.release(handle.documentId);
  }

  /** Aborts an entity's satisfaction request and forgets it, releasing its load ops. */
  private _dropSatisfactionRequest(objectId: string): void {
    this._satisfactionRequests.get(objectId as EntityId)?.abort();
    this._satisfactionRequests.delete(objectId as EntityId);
    this._satisfactionSubscriptions.get(objectId as EntityId)?.();
    this._satisfactionSubscriptions.delete(objectId as EntityId);
  }

  /**
   * Drops a released object's request once it settles, deferred by a turn because the abort runs from
   * inside the request's own state change, where releasing its ops would cancel IO mid-flight.
   */
  private _dropSatisfactionRequestWhenSettled(objectId: string, request: RefResolverRequest): void {
    if (this._ctx == null) {
      return;
    }
    const unsubscribe = request.stateChanged.on(this._ctx, () => {
      if (!isSettled(request)) {
        return;
      }
      unsubscribe();
      setTimeout(() => {
        if (!this._objects.has(objectId) && this._satisfactionRequests.get(objectId as EntityId) === request) {
          this._dropSatisfactionRequest(objectId);
        }
      });
    });
  }

  private _processDocumentUpdate(event: ChangeEvent<DatabaseDirectory>): DocumentChanges {
    const { inlineChangedObjects, linkedDocuments } = getInlineAndLinkChanges(event);
    const createdObjectIds: string[] = [];
    const objectsToRebind: string[] = [];
    for (const updatedObject of inlineChangedObjects) {
      const objectCore = this._objects.get(updatedObject);
      if (!objectCore) {
        createdObjectIds.push(updatedObject);
      } else if (
        objectCore.handle?.url != null &&
        event.handle.url != null &&
        objectCore.handle.url !== event.handle.url
      ) {
        log.verbose('object bound to incorrect document, going to rebind', {
          updatedObject,
          documentUrl: objectCore.handle.url,
          actualUrl: event.handle.url,
        });
        objectsToRebind.push(updatedObject);
      }
    }

    return {
      updatedObjectIds: inlineChangedObjects,
      objectsToRebind,
      createdObjectIds,
      linkedDocuments,
    };
  }

  private _unsubscribeFromHandles(): void {
    for (const docHandle of Object.values(this._repoProxy.handles)) {
      docHandle.off('change', this._onDocumentUpdate);
    }
  }

  private _onObjectDocumentLoaded({ handle, objectId }: ObjectDocumentLoaded): void {
    handle.off('change', this._onDocumentUpdate);
    handle.on('change', this._onDocumentUpdate);
    countWork('echo.objectDocumentsLoaded');

    const existing = this._objects.get(objectId);
    if (existing?.snapshot) {
      existing.bind({ db: this, docHandle: handle, path: existing.mountPath, assignFromLocalState: false });
      this._onObjectBoundToDocument(handle, objectId);
    }
    const core = existing ?? this._createObjectInDocument(handle, objectId);

    // A ready handle does not mean the body arrived: a linked document settles empty while the peer
    // holding it is still replicating. The core is created either way so the object keeps one
    // identity across the body landing, and carries the absence as `isBodyAvailable` — which the
    // read paths check — instead of encoding it as a missing core.
    if (!core.isBodyAvailable) {
      this._onObjectUnavailable({ handle, objectId });
      return;
    }

    // The body was previously marked unavailable but its bytes have now arrived (e.g. a peer
    // eventually delivered them); clear the mark so any in-flight body load resolves afresh.
    this._markObjectAvailable(objectId);
    // Surface the new body. The query pipeline re-evaluates strong-dep satisfaction through the
    // resolver; dependents whose closure includes this entity are woken by their satisfaction
    // request's load op transitioning to ready.
    this._scheduleThrottledUpdate([objectId]);
  }

  private _createInlineObjects(docHandle: DocHandleProxy<DatabaseDirectory>, objectIds: string[]): void {
    for (const id of objectIds) {
      invariant(!this._objects.has(id));
      this._createObjectInDocument(docHandle, id);
    }
  }

  private _createObjectInDocument(docHandle: DocHandleProxy<DatabaseDirectory>, objectId: string): ObjectCore {
    invariant(!this._objects.get(objectId));
    const core = new ObjectCore();
    core.id = objectId;
    this._objects.set(core.id, core);
    this._markObjectAvailable(objectId);
    this._onObjectBoundToDocument(docHandle, objectId);
    core.bind({
      db: this,
      docHandle,
      path: ['objects', core.id],
      assignFromLocalState: false,
    });

    return core;
  }

  /**
   * Whether the entity's full strong-dependency closure is loaded and satisfied.
   * Query paths require this before surfacing an object. Delegated to the resolver, so it spans
   * same-space / cross-space db objects, feed-queue objects, and registry types uniformly.
   */
  areStrongDepsSatisfied(core: ObjectCore): boolean {
    return this._areDepsSatisfied(core);
  }

  /**
   * Whether the entity's strong-dependency closure has settled, satisfied or not.
   * Naming an exact id is not a discovery query: the caller already holds the id, so an
   * unreachable dependency must resolve the lookup rather than stall it forever.
   */
  areStrongDepsResolved(core: ObjectCore): boolean {
    return this._areDepsResolved(core);
  }

  private _areDepsSatisfied(core: ObjectCore): boolean {
    return this._ensureSatisfactionRequest(core).state === 'ready';
  }

  /**
   * Returns true when the strong-dep closure is either satisfied OR settled `unavailable` at the
   * disk ceiling. Used by `loadObjectCoreById` so it resolves (with `undefined`) instead of waiting
   * forever when a dependency is unreachable on disk.
   */
  private _areDepsResolved(core: ObjectCore): boolean {
    const state = this._ensureSatisfactionRequest(core).state;
    return state === 'ready' || state === 'unavailable';
  }

  /**
   * Returns (creating on first use) the closure-aware satisfaction request for an entity. Surfacing
   * subscribes to its state changes so the query pipeline re-evaluates as the closure loads.
   */
  private _ensureSatisfactionRequest(core: ObjectCore): RefResolverRequest {
    // The id, not the core: a closure over `core` is a strong reference from the database context's
    // dispose list to every object ever surfaced by a query, which no eviction could undo.
    const objectId = core.id;
    let request = this._satisfactionRequests.get(objectId);
    if (request == null) {
      this._refResolver ??= this._hypergraph.createRefResolver({ context: { space: this._spaceId } });
      const uri = EID.make({ spaceId: this._spaceId, entityId: objectId });
      request = this._refResolver.resolve(uri, { source: 'disk' });
      this._satisfactionRequests.set(objectId, request);
      this._satisfactionSubscriptions.set(
        objectId,
        request.stateChanged.on(this._ctx!, () => this._scheduleThrottledUpdate([objectId])),
      );
    }
    return request;
  }

  /**
   * Clears a stale `_unavailableObjects` mark once an object's body becomes available, waking any
   * in-flight body load. The mark is set when an id is probed (`diskOnly`) while absent from the
   * space directory; an object later materialized locally (added) or whose document arrives must
   * clear it, otherwise `diskOnly` loads keep short-circuiting to `undefined`.
   */
  private _markObjectAvailable(objectId: string): void {
    if (this._unavailableObjects.delete(objectId)) {
      this._scheduleThrottledUpdate([objectId]);
    }
  }

  /**
   * Clears the unavailable mark of every object whose body has now landed in its document — the
   * other half of a core created against a document that settled without one.
   */
  private _markBodiesAvailable(objectIds: string[]): void {
    for (const objectId of objectIds) {
      if (this._unavailableObjects.has(objectId) && this._objects.get(objectId)?.isBodyAvailable) {
        this._markObjectAvailable(objectId);
      }
    }
  }

  private _onObjectUnavailable({ objectId }: ObjectUnavailable): void {
    if (this._unavailableObjects.has(objectId)) {
      return;
    }
    this._unavailableObjects.add(objectId);
    // Wake any in-flight body load so it settles `unavailable` instead of hanging; satisfaction
    // requests whose closure includes this body re-evaluate via their load op transitioning.
    this._scheduleThrottledUpdate([objectId]);
  }

  private _rebindObjects(docHandle: DocHandleProxy<DatabaseDirectory>, objectIds: string[]): void {
    for (const objectId of objectIds) {
      const objectCore = this._objects.get(objectId);
      invariant(objectCore);
      objectCore.bind({
        db: this,
        docHandle,
        path: objectCore.mountPath,
        assignFromLocalState: false,
      });
      this._onObjectBoundToDocument(docHandle, objectId);
    }
  }

  // ── Update scheduling ────────────────────────────────────────────────────

  @trace.span({ showInBrowserTimeline: true, showInRemoteTracing: false })
  private _emitDbUpdateEvents(_ctx: Context): void {
    // Mid root-document swap there is nothing to emit against — listeners resolve cores through the
    // root. Pending ids are kept; `updateSpaceState` re-triggers after the swap.
    if (!this._spaceRootDocHandle) {
      return;
    }
    const fullUpdateIds = [...this._objectsForNextUpdate];
    const allDbUpdates = new Set([...this._objectsForNextUpdate, ...this._objectsForNextDbUpdate]);
    this._objectsForNextUpdate.clear();
    this._objectsForNextDbUpdate.clear();

    batchEvents(() => {
      if (allDbUpdates.size > 0) {
        this._updateEvent.emit({
          spaceId: this.spaceId,
          itemsUpdated: [...allDbUpdates].map((id) => ({ id })),
        });
      }
      this._emitObjectUpdateEvent(fullUpdateIds);
    });
  }

  private _scheduleThrottledUpdate(objectId: string[]): void {
    for (const id of objectId) {
      this._objectsForNextUpdate.add(id);
    }
    this._updateScheduler.forceTrigger();
  }

  /** `coalesce` lets the emission wait out the rate; otherwise it runs at once. */
  private _scheduleThrottledDbUpdate(objectId: string[], { coalesce = false }: { coalesce?: boolean } = {}): void {
    for (const id of objectId) {
      this._objectsForNextDbUpdate.add(id);
    }
    if (coalesce) {
      this._updateScheduler.trigger();
    } else {
      this._updateScheduler.forceTrigger();
    }
  }
}

const RPC_TIMEOUT = 20_000;

const DOCUMENT_RETENTION: Required<DocumentRetention> = { ttl: 30_000, budget: 50 };

type PassiveUpdate = ClientDocHandleEvents<DatabaseDirectory>['passive'];
